<template>
  <div class="quotation-edit">
    <h4 class="mb-3">등록 세부정보</h4>

    <!-- 로딩 중일 때 표시 -->
    <div v-if="isProcessing" class="loading-container">
      <b-spinner variant="primary"></b-spinner>
      <p>{{ progressMessage || '등록 세부정보 작업 중...' }}</p>
    </div>

    <!-- 등록 세부정보 편집 폼 -->
    <div v-else class="quotation-form">
      <div class="card mb-4">
        <div class="card-header bg-light d-flex justify-content-between align-items-center">
          <span>쿠팡 등록 정보</span>
          <div class="d-flex align-items-center">
            <b-button variant="primary" size="sm" class="mr-2" @click="processQuotationWithAI" :disabled="isProcessing">
              <i class="las la-robot"></i> AI 처리
            </b-button>
          </div>
        </div>
        <div class="card-body">

          <div v-if="!hasCoupangDoc" class="alert alert-warning">
            <i class="las la-exclamation-triangle"></i> 쿠팡 문서 정보가 없습니다. 먼저 상품 정보를 불러오세요.
            <br>
            <small>상품 ID: {{ product ? product.registrationId : '없음' }}</small>
          </div>

          <!-- 쿠팡 문서가 있을 경우 필드 나열 -->
          <template v-else>
            <!-- 1. startPage 섹션 -->
            <div class="section mb-4">
              <h5 class="bg-primary text-white p-2 rounded">1. Start Page (시작 페이지)</h5>
              <div class="p-3 border rounded">
                <div class="row">
                  <div v-for="(_, field) in docData.startPage" :key="`startPage.${field}`" class="col-md-12">
                    <div class="form-group">
                      <label>{{ getFieldTitle('startPage', field) }} <span v-if="isRequiredField('startPage', field)"
                          class="text-danger">*</span></label>
                      <small v-if="getFieldDescription('startPage', field)" class="form-text text-muted mb-1">{{
                        getFieldDescription('startPage', field) }}</small>
                      <div v-if="field === 'productName'" class="position-relative">
                        <input type="text" class="form-control" v-model="docData.startPage[field]" readonly>
                        <div class="alert alert-info mt-1 mb-0 py-1 px-2">
                          <i class="las la-info-circle"></i> 상품명은 SEO 설정에서 수정해주세요.
                        </div>
                      </div>
                      <input v-else type="text" class="form-control" v-model="docData.startPage[field]"
                        :readonly="field === 'categoryPath'"
                        @blur="field !== 'categoryPath' ? onFieldBlur(`startPage.${field}`, docData.startPage[field]) : null">
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 2. productPage 섹션 -->
            <div class="section mb-4">
              <h5 class="bg-info text-white p-2 rounded">2. Product Page (상품 페이지)</h5>
              <div class="p-3 border rounded">
                <!-- 2.1 기본 필드 -->
                <h6 class="border-bottom pb-2 mb-3">기본 정보</h6>
                <div class="row">
                  <div v-for="(_, field) in getDirectFields(docData.productPage)" :key="`productPage.${field}`"
                    class="col-md-12">
                    <div class="form-group">
                      <label>{{ getFieldTitle('productPage', field) }} <span
                          v-if="isRequiredField('productPage', field)" class="text-danger">*</span></label>
                      <small v-if="getFieldDescription('productPage', field)" class="form-text text-muted mb-1">{{
                        getFieldDescription('productPage', field) }}</small>

                      <!-- 필드 타입에 따른 입력 컴포넌트 -->
                      <select v-if="hasFieldOptions('productPage', field) || field === 'brand'" class="form-control"
                        v-model="docData.productPage[field]"
                        @change="onDropdownChange(`productPage.${field}`, docData.productPage[field])">
                        <!-- 브랜드명 필드 특수 처리 -->
                        <template v-if="field === 'brand'">
                          <option value="">브랜드 없음</option>
                          <!-- 기본 설정의 브랜드명을 개별 옵션으로 분리 -->
                          <template v-if="$store.state.settings[0]?.defaultSettings?.brand">
                            <option v-for="(brandName, brandIndex) in $store.state.settings[0].defaultSettings.brand.split(',')" :key="`brand-${brandIndex}-${brandName.trim()}`"
                              :value="brandName.trim()">{{ brandName.trim() }}</option>
                          </template>
                        </template>
                        <!-- 기본 옵션 목록 (브랜드 필드가 아닌 경우만) -->
                        <template v-else>
                          <option v-for="(option, optIndex) in getFieldOptions('productPage', field)" :key="`${field}-${optIndex}-${option}`" :value="option">{{
                            option }}</option>
                        </template>
                      </select>

                      <!-- 모델번호 필드 - 50자 제한 추가 -->
                      <template v-else-if="field === 'modelNumber'">
                        <div class="position-relative">
                          <input type="text" class="form-control" v-model="docData.productPage[field]"
                                 maxlength="50" @input="enforceModelNumberLimit"
                                 @blur="onFieldBlur(`productPage.${field}`, docData.productPage[field])">
                          <div class="d-flex justify-content-between mt-1">
                            <small class="text-muted">모델번호는 최대 50자까지 입력 가능합니다.</small>
                            <small :class="{ 'text-danger': modelNumberLength > 45, 'text-warning': modelNumberLength > 40 && modelNumberLength <= 45, 'text-muted': modelNumberLength <= 40 }">
                              {{ modelNumberLength }}/50자
                            </small>
                          </div>
                        </div>
                      </template>

                      <!-- 검색 태그 필드 - 편집 가능 -->
                      <template v-else-if="field === 'searchTags'">
                        <div class="position-relative">
                          <textarea class="form-control"
                                    v-model="docData.productPage[field]"
                                    rows="3"
                                    :maxlength="150"
                                    @input="onSearchTagsChange"
                                    @blur="onFieldBlur(`productPage.${field}`, docData.productPage[field])"
                                    placeholder="검색 태그를 쉼표(,)로 구분하여 입력하세요"></textarea>
                          <div class="d-flex justify-content-between mt-1">
                            <small class="text-muted">쉼표(,)로 구분하여 입력하세요. 최대 150자</small>
                            <small
                              :class="{ 'text-danger': searchTagsCount > 140, 'text-warning': searchTagsCount > 120 && searchTagsCount <= 140, 'text-muted': searchTagsCount <= 120 }">
                              {{ searchTagsCount }}/150자
                            </small>
                          </div>
                        </div>
                      </template>

                      <!-- 기타 필드는 일반 텍스트 입력 -->
                      <input v-else type="text" class="form-control" v-model="docData.productPage[field]"
                             @blur="onFieldBlur(`productPage.${field}`, docData.productPage[field])">
                    </div>
                  </div>
                </div>

                <!-- 2.2 commonAttributes 필드 -->
                <div v-if="docData.productPage.commonAttributes" class="mt-4">
                  <h6 class="border-bottom pb-2 mb-3">가격 정보</h6>
                  <div class="row">
                    <div v-for="(_, field) in getPriceFields" :key="`commonAttributes.${field}`" class="col-md-12">
                      <div class="form-group">
                        <label>{{ getFieldTitle('productPage.commonAttributes', field) }} <span
                            v-if="isRequiredField('productPage.commonAttributes', field)"
                            class="text-danger">*</span></label>
                        <small v-if="getFieldDescription('productPage.commonAttributes', field)"
                          class="form-text text-muted mb-1">{{
                            getFieldDescription('productPage.commonAttributes',
                              field)
                          }}</small>

                        <div v-if="field.includes('Price') || field.includes('price')" class="position-relative">
                          <!-- 공급가와 판매가는 편집 가능 -->
                          <input v-if="field === 'purchasePrice' || field === 'coupangSalePrice'"
                                 type="number"
                                 class="form-control"
                                 v-model.number="docData.productPage.commonAttributes[field]"
                                 @input="onPriceFieldChange(field)"
                                 @blur="onFieldBlur(`productPage.commonAttributes.${field}`, docData.productPage.commonAttributes[field])"
                                 :placeholder="field === 'purchasePrice' ? '공급가 입력' : '판매가 입력'">
                          <!-- 기타 가격 필드는 readonly -->
                          <template v-else>
                            <input type="number" class="form-control" v-model.number="docData.productPage.commonAttributes[field]" readonly>
                            <div class="alert alert-info mt-1 mb-0 py-1 px-2">
                              <i class="las la-info-circle"></i> 이 가격 정보는 가격 설정에서 수정해주세요.
                            </div>
                          </template>
                        </div>
                        <input v-else type="text" class="form-control"
                          v-model="docData.productPage.commonAttributes[field]"
                          @blur="onFieldBlur(`productPage.commonAttributes.${field}`, docData.productPage.commonAttributes[field])">
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 2.3 노출 속성 -->
                <div
                  v-if="docData.productPage.commonAttributes && docData.productPage.commonAttributes.exposedAttributes"
                  class="mt-4">
                  <h6 class="border-bottom pb-2 mb-3">노출 속성</h6>
                  <small class="form-text text-muted mb-2 d-block">상품 상세 페이지에 노출되는 상품의 주요 속성 정보입니다.</small>

                  <div v-for="(attr, index) in docData.productPage.commonAttributes.exposedAttributes"
                    :key="`exposed-${index}`" class="mb-2">
                    <div class="d-flex">
                      <div class="d-flex align-items-center" style="width: 40%">
                        <input type="text" class="form-control mr-1" v-model="attr.attributeName" readonly
                          placeholder="속성명">
                        <!-- 예시 값이 있는 경우 툴팁 아이콘 표시 -->
                        <i v-if="getAttributeExample('exposedAttributes', attr.attributeName)"
                           class="las la-question-circle text-info tooltip-icon"
                           v-b-tooltip.hover.right
                           :title="`예시: ${getAttributeExample('exposedAttributes', attr.attributeName)}`"></i>
                      </div>

                      <!-- 드롭다운 값이 있으면 select 표시, 없으면 input 표시 -->
                      <select v-if="getExposedAttributeOptions(attr.attributeName).length > 0"
                        class="form-control ml-2" v-model="attr.attributeValue"
                        @change="onAttributeDropdownChange('exposed', index, attr.attributeValue)">
                        <option v-for="(option, optIndex) in getExposedAttributeOptions(attr.attributeName)"
                          :key="`exposed-${index}-${optIndex}-${option}`" :value="option">{{ option }}</option>
                      </select>
                      <input v-else type="text" class="form-control ml-2" v-model="attr.attributeValue"
                             placeholder="속성값" @blur="onAttributeBlur('exposed', index, attr.attributeValue)">
                    </div>
                  </div>
                </div>

                <!-- 2.4 비노출 속성 -->
                <div v-if="docData.productPage.unexposedAttributes" class="mt-4">
                  <h6 class="border-bottom pb-2 mb-3">비노출 속성</h6>
                  <small class="form-text text-muted mb-2 d-block">상품 상세 페이지에 노출되지 않는 상품의 부가 정보입니다.</small>

                  <div v-for="(attr, index) in docData.productPage.unexposedAttributes" :key="`unexposed-${index}`"
                    class="mb-2">
                    <div class="d-flex">
                      <div class="d-flex align-items-center" style="width: 40%">
                        <input type="text" class="form-control mr-1" v-model="attr.attributeName" readonly
                          placeholder="속성명">
                        <!-- 예시 값이 있는 경우 툴팁 아이콘 표시 -->
                        <i v-if="getAttributeExample('unexposedAttributes', attr.attributeName)"
                           class="las la-question-circle text-info tooltip-icon"
                           v-b-tooltip.hover.right
                           :title="`예시: ${getAttributeExample('unexposedAttributes', attr.attributeName)}`"></i>
                      </div>

                      <!-- 드롭다운 값이 있으면 select 표시, 없으면 input 표시 -->
                      <select v-if="getUnexposedAttributeOptions(attr.attributeName).length > 0"
                        class="form-control ml-2"
                        :value="attr.attributeValue === null ? '' : attr.attributeValue"
                        @change="e => onUnexposedAttributeChange(e, attr, index)">
                        <option value="">해당사항없음</option>
                        <option v-for="(option, optIndex) in getUnexposedAttributeOptions(attr.attributeName)"
                          :key="`unexposed-${index}-${optIndex}-${option}`" :value="option">{{ option }}</option>
                      </select>
                      <template v-else>
                        <!-- attributeValue가 null인 경우 '해당사항없음' 표시 -->
                        <div v-if="attr.attributeValue === null" class="form-control text-muted ml-2"
                             style="display: flex; align-items: center;"
                             @click="attr.attributeValue = ''">
                          해당사항없음
                        </div>
                        <input v-else type="text" class="form-control ml-2" v-model="attr.attributeValue"
                          placeholder="속성값" @focus="attr.attributeValue = attr.attributeValue || ''"
                          @blur="onAttributeBlur('unexposed', index, attr.attributeValue)">
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 3. imagePage 섹션 -->
            <div class="section mb-4">
              <h5 class="bg-success text-white p-2 rounded">3. Image Page (이미지 페이지)</h5>
              <div class="p-3 border rounded">
                <!-- 3.1 이미지 정보 -->
                <div v-if="docData.imagePage && docData.imagePage.images" class="mb-3">
                  <h6 class="border-bottom pb-2 mb-3">기본 이미지</h6>
                  <div class="row">
                    <div v-for="(_, field) in getDirectImageFields" :key="`imagePage.images.${field}`"
                      class="col-md-12">
                      <div class="form-group">
                        <label>{{ getFieldTitle('imagePage.images', field) }} <span
                            v-if="isRequiredField('imagePage.images', field)" class="text-danger">*</span></label>
                        <small v-if="getFieldDescription('imagePage.images', field)"
                          class="form-text text-muted mb-1">{{
                            getFieldDescription('imagePage.images', field) }}</small>
                        <input type="text" class="form-control" v-model="docData.imagePage.images[field]"
                               @blur="onFieldBlur(`imagePage.images.${field}`, docData.imagePage.images[field])">
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 3.2 라벨 이미지 -->
                <div v-if="docData.imagePage && docData.imagePage.images && docData.imagePage.images.labelImages"
                  class="mt-4">
                  <h6 class="border-bottom pb-2 mb-3">라벨 이미지</h6>
                  <small class="form-text text-muted mb-2 d-block">제품 라벨, 사이즈 차트 등의 이미지 정보입니다.</small>

                  <div v-for="(label, index) in docData.imagePage.images.labelImages" :key="`label-${index}`"
                    class="mb-2">
                    <div class="d-flex">
                      <input type="text" class="form-control mr-2" v-model="label.labelImageType" readonly
                        style="width: 40%" placeholder="라벨 타입">

                      <input type="text" class="form-control" v-model="label.labelImageFiles" placeholder="이미지 파일"
                             @blur="onFieldBlur(`imagePage.images.labelImages.${index}.labelImageFiles`, label.labelImageFiles)">
                    </div>
                  </div>
                </div>

                <!-- 3.3 상세 정보 -->
                <div v-if="docData.imagePage && docData.imagePage.details" class="mt-4">
                  <h6 class="border-bottom pb-2 mb-3">상세 정보</h6>
                  <div class="row">
                    <div v-for="(_, field) in docData.imagePage.details" :key="`imagePage.details.${field}`"
                      class="col-md-12">
                      <div class="form-group">
                        <label>{{ getFieldTitle('imagePage.details', field) }} <span
                            v-if="isRequiredField('imagePage.details', field)" class="text-danger">*</span></label>
                        <small v-if="getFieldDescription('imagePage.details', field)"
                          class="form-text text-muted mb-1">{{
                            getFieldDescription('imagePage.details', field) }}</small>

                        <textarea v-if="field === 'htmlProductDetailContent'" class="form-control"
                          v-model="docData.imagePage.details[field]" rows="1"
                          @blur="onFieldBlur(`imagePage.details.${field}`, docData.imagePage.details[field])"></textarea>
                        <input v-else type="text" class="form-control" v-model="docData.imagePage.details[field]"
                               @blur="onFieldBlur(`imagePage.details.${field}`, docData.imagePage.details[field])">
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 4. legalPage 섹션 -->
            <div class="section mb-4">
              <h5 class="bg-danger text-white p-2 rounded">4. Legal Page (법적 정보)</h5>
              <div class="p-3 border rounded">
                <!-- 4.1 기본 법적 정보 -->
                <h6 class="border-bottom pb-2 mb-3">기본 법적 정보</h6>
                <div class="row">
                  <div v-for="(_, field) in getDirectLegalFields" :key="`legalPage.${field}`" class="col-md-12">
                    <div class="form-group">
                      <label>{{ getFieldTitle('legalPage', field) }} <span v-if="isRequiredField('legalPage', field)"
                          class="text-danger">*</span></label>
                      <small v-if="getFieldDescription('legalPage', field)" class="form-text text-muted mb-1">{{
                        getFieldDescription('legalPage', field) }}</small>

                      <select v-if="hasFieldOptions('legalPage', field)" class="form-control"
                        v-model="docData.legalPage[field]"
                        @change="onDropdownChange(`legalPage.${field}`, docData.legalPage[field])">
                        <option v-for="option in getFieldOptions('legalPage', field)" :key="option" :value="option">{{
                          option }}</option>
                      </select>

                      <input v-else type="text" class="form-control" v-model="docData.legalPage[field]"
                             @blur="onFieldBlur(`legalPage.${field}`, docData.legalPage[field])">
                    </div>
                  </div>
                </div>

                <!-- 4.2 인증 정보 -->
                <div v-if="docData.legalPage && docData.legalPage.certificates" class="mt-4">
                  <h6 class="border-bottom pb-2 mb-3">인증 정보</h6>
                  <small class="form-text text-muted mb-2 d-block">상품의 안전 인증 정보입니다.</small>

                  <div v-for="(cert, index) in docData.legalPage.certificates" :key="`cert-${index}`" class="mb-2">
                    <div class="d-flex">
                      <input type="text" class="form-control mr-2" v-model="cert.certificateName" readonly
                        style="width: 40%" placeholder="인증명">

                      <input type="text" class="form-control" v-model="cert.certificateValue" placeholder="인증 번호"
                             @blur="onFieldBlur(`legalPage.certificates.${index}.certificateValue`, cert.certificateValue)">
                    </div>
                  </div>
                </div>

                <!-- 4.3 상품 고시 정보 -->
                <div v-if="docData.legalPage && docData.legalPage.notices" class="mt-4">
                  <h6 class="border-bottom pb-2 mb-3">상품 고시 정보</h6>
                  <small class="form-text text-muted mb-2 d-block">전자상거래법에 따른 필수 표시 정보입니다.</small>

                  <div v-for="(notice, index) in docData.legalPage.notices" :key="`notice-${index}`" class="mb-2">
                    <div class="d-flex">
                      <input type="text" class="form-control mr-2" v-model="notice.noticeItemName" readonly
                        style="width: 40%" placeholder="항목">

                      <!-- 드롭다운 값이 있으면 select 표시, 없으면 input 표시 -->
                      <select v-if="getNoticeItemOptions(notice.noticeItemName).length > 0"
                        class="form-control" v-model="notice.noticeItemValue"
                        @change="onFieldBlur(`legalPage.notices.${index}.noticeItemValue`, notice.noticeItemValue)">
                        <option v-for="(option, optIndex) in getNoticeItemOptions(notice.noticeItemName)"
                          :key="`notice-${index}-${optIndex}-${option}`" :value="option">{{ option }}</option>
                      </select>
                      <input v-else type="text" class="form-control" v-model="notice.noticeItemValue" placeholder="값"
                             @blur="onFieldBlur(`legalPage.notices.${index}.noticeItemValue`, notice.noticeItemValue)">
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 5. logisticsPage 섹션 -->
            <div class="section mb-4">
              <h5 class="bg-dark text-white p-2 rounded">5. Logistics Page (물류 정보)</h5>
              <div class="p-3 border rounded">
                <div class="row">
                  <div v-for="(_, field) in docData.logisticsPage" :key="`logisticsPage.${field}`"
                    class="col-md-12">
                    <div class="form-group">
                      <label>{{ getFieldTitle('logisticsPage', field) }} <span
                          v-if="isRequiredField('logisticsPage', field)" class="text-danger">*</span></label>
                      <small v-if="getFieldDescription('logisticsPage', field)" class="form-text text-muted mb-1">{{
                        getFieldDescription('logisticsPage', field) }}</small>

                      <!-- 필드 타입에 따른 입력 컴포넌트 -->
                      <select v-if="hasFieldOptions('logisticsPage', field)" class="form-control"
                        v-model="docData.logisticsPage[field]"
                        @change="onDropdownChange(`logisticsPage.${field}`, docData.logisticsPage[field])">
                        <option v-for="(option, optIndex) in getFieldOptions('logisticsPage', field)" :key="`logistics-${field}-${optIndex}-${option}`" :value="option">
                          {{
                            option }}</option>
                      </select>

                      <input v-else-if="field === 'totalSKUsInBox' || field === 'daysToExpiration'" type="number"
                        class="form-control" v-model.number="docData.logisticsPage[field]"
                        @blur="onFieldBlur(`logisticsPage.${field}`, docData.logisticsPage[field])">

                      <input v-else type="text" class="form-control" v-model="docData.logisticsPage[field]"
                             @blur="onFieldBlur(`logisticsPage.${field}`, docData.logisticsPage[field])">
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 하단 버튼 -->
            <div class="mt-4 text-center">
              <b-button variant="secondary" class="mr-2" @click="resetQuotation">
                <i class="las la-undo"></i> 초기화
              </b-button>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- 모든 옵션 적용 토글 패널 (개선된 버전) -->
    <div class="improved-toggle-panel" v-if="product">
      <div class="toggle-bubble d-flex align-items-center" :class="{ 'active': applyToAllOptions }">
        <div class="toggle-title mr-3">모든 옵션에 적용</div>
        <div class="toggle-switch-area mr-2">
          <label class="bubble-switch mb-0">
            <input type="checkbox"
                   v-model="applyToAllOptions"
                   @change="onApplyToAllOptionsChange">
            <span class="bubble-slider"></span>
          </label>
        </div>
        <div class="toggle-actions">
          <b-button
            variant="success"
            size="sm"
            :disabled="isProcessing || isSaving"
            @click="handleSaveButton">
            <i class="las la-save"></i> 저장
          </b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import quotationService from '../../services/works/quotationService';
import PracticalSchemaValidator from '../../services/validation/schemaValidator';
import confirmInPageMixin from '@/mixins/confirmInPageMixin';
import { mergeUserEdits, runDiscardingUserEdits, quotationEditCount } from '../../services/works/userEditGuard';

export default {
  mixins: [confirmInPageMixin],
  name: "QuotationEdit",
  data() {
    return {
      // 스키마 정보 저장
      schema: null,

      // 모든 옵션에 적용 스위치 (기본값: false)
      applyToAllOptions: false,

      // UI를 위한 상태 변수
      modelNumberLength: 0,
      searchTagsCount: 0,

      // 실시간 변경 감지를 위한 변수
      isInitializing: false,

      // Pending 변경사항 관리 (OFF 모드에서 사용)
      pendingChanges: {},
      hasPendingChanges: false,

      // 저장 진행 상태 (버튼 비활성화/스피너 표시용)
      isSaving: false,

      // 자동 저장 기능 사용 여부(요청에 따라 기본 false)
      autoSaveEnabled: false,


      // 쿠팡 문서 데이터
      docData: {
        startPage: {
          productName: '',
          categoryPath: ''
        },
        productPage: {
          modelNumber: '',
          brand: '',
          commonAttributes: {
            exposedAttributes: [],
            purchasePrice: 0,
            coupangSalePrice: 0,
            productBarcode: '바코드 없음(쿠팡 바코드 생성 요청)'
            // msrp/osrp는 스키마에 따라 동적으로 추가됨
          },
          manufacturer: '',
          businessType: '제조사',
          taxationSchema: '과세',
          importType: '수입대상아님',
          searchTags: '',
          unexposedAttributes: []
        },
        imagePage: {
          images: {
            mainImage: '',
            additionalImage: '',
            labelImages: []
          },
          details: {
            detailedImage: '',
            htmlProductDetailContent: '',
            altText: ''
          }
        },
        legalPage: {
          kcMarkType: '해당사항없음',
          certificates: [],
          adCert: '',
          notices: []
        },
        logisticsPage: {
          // 물류 정보는 AI가 생성한 경우에만 표시 (하드코딩 제거)
        },
        sourcingPage: {
          sourcingChannelType: '',
          sourcingChannelId: ''
        }
      }
    };
  },
  computed: {
    // Vuex 상태 매핑
    ...mapState({
      product: state => state.selectedRocketProduct,
      selectedOption: state => state.selectedOption,
      isProcessing: state => state.processing,
      progressMessage: state => state.progressMessage
    }),

    // 쿠팡 문서 존재 여부
    hasCoupangDoc() {
      return this.currentCoupangDoc !== null;
    },

    // 스키마 존재 여부
    hasSchema() {
      return this.product &&
        this.product.rocketRegInfo &&
        this.product.rocketRegInfo.schemaString;
    },

    // 현재 선택된 옵션의 coupangDoc
    currentCoupangDoc() {
      // 결과 변수
      let result = null;

      // 상품의 coupangDoc 확인
      if (this.product && this.product.coupangDoc) {
        console.log('currentCoupangDoc: 상품 레벨의 coupangDoc 발견');

        // 선택된 옵션이 있는 경우 해당 옵션 인덱스의 jsonDocument 항목 사용
        if (this.selectedOption && this.product.options) {
          // 선택된 옵션의 인덱스 찾기
          const optionIndex = this.product.options.findIndex(opt =>
            opt.optionNumber === this.selectedOption.optionNumber);

          console.log(`currentCoupangDoc: 선택된 옵션 인덱스: ${optionIndex}, 옵션 번호: ${this.selectedOption.optionNumber}`);

          // 해당 인덱스의 jsonDocument 항목이 있는지 확인
          if (optionIndex >= 0 &&
              this.product.coupangDoc.jsonDocument &&
              this.product.coupangDoc.jsonDocument.length > optionIndex) {

            // 깊은 복사를 통해 새 coupangDoc 객체 생성
            result = {
              ...this.product.coupangDoc,
              // jsonDocument 배열에서 해당 옵션 인덱스의 항목만 포함
              jsonDocument: [this.deepClone(this.product.coupangDoc.jsonDocument[optionIndex])]
            };

            console.log(`currentCoupangDoc: 옵션 인덱스 ${optionIndex}의 jsonDocument 항목 사용`);
          } else {
            console.log(`currentCoupangDoc: 옵션 인덱스 ${optionIndex}에 해당하는 jsonDocument 항목이 없음, 첫 번째 항목 사용`);
            // 해당 인덱스의 항목이 없으면 첫 번째 항목 사용
            result = this.deepClone(this.product.coupangDoc);
          }
        } else {
          // 선택된 옵션이 없으면 첫 번째 jsonDocument 항목 사용
          console.log('currentCoupangDoc: 선택된 옵션이 없음, 첫 번째 jsonDocument 항목 사용');
          result = this.deepClone(this.product.coupangDoc);
        }
      }

      // 이전 방식 호환성 유지 (옵션 레벨의 coupangDoc이 있는 경우)
      if (!result && this.selectedOption && this.selectedOption.coupangDoc) {
        console.log('currentCoupangDoc: 옵션 레벨의 coupangDoc 발견 (이전 방식)');
        result = this.deepClone(this.selectedOption.coupangDoc);
      }

      // coupangDoc를 있는 그대로 표시 (자동 수정 제거)

      return result;
    },
    // productPage의 commonAttributes 중 가격 관련 필드만 필터링 (스키마 기반)
    getPriceFields() {
      if (!this.docData.productPage || !this.docData.productPage.commonAttributes) return {};

      const result = {};
      const fields = this.getDirectFields(this.docData.productPage.commonAttributes);

      for (const field in fields) {
        // 가격 관련 필드이면서 스키마에서 허용하는 필드만 포함
        if ((field.includes('Price') || field.includes('price') || field === 'productBarcode' || field === 'msrp' || field === 'osrp') &&
            this.isFieldAllowedInSchema('productPage.commonAttributes', field)) {
          result[field] = fields[field];
        }
      }

      return result;
    },

    // imagePage의 직접 이미지 필드 (labelImages 아닌 것)
    getDirectImageFields() {
      if (!this.docData.imagePage || !this.docData.imagePage.images) return {};

      const result = {};
      const fields = this.getDirectFields(this.docData.imagePage.images);

      for (const field in fields) {
        if (field !== 'labelImages') {
          result[field] = fields[field];
        }
      }

      return result;
    },

    // legalPage의 직접 필드 (certificates, notices, adCert 아닌 것)
    getDirectLegalFields() {
      if (!this.docData.legalPage) return {};

      const result = {};
      const fields = this.getDirectFields(this.docData.legalPage);

      for (const field in fields) {
        if (field !== 'certificates' && field !== 'notices' && field !== 'adCert') {
          result[field] = fields[field];
        }
      }

      return result;
    },

    // 🔥 새로운 검증 결과 표시
    validationSummary() {
      const validation = this.validateSchema();

      if (!validation) {
        return {
          percentage: '0.0',
          status: 'danger',
          message: '검증 정보 없음',
          chineseIssue: false,
          errors: []
        };
      }

      const percentage = (validation.completionRate * 100).toFixed(1);

      // 중국어 포함 여부 확인
      const chineseIssue = validation.errors.some(error => error.includes('중국어'));

      let status = 'success';
      if (chineseIssue) {
        status = 'danger'; // 중국어 포함시 위험
      } else if (validation.completionRate < 0.7) {
        status = 'warning';
      }

      return {
        percentage,
        status,
        message: `완성도 ${percentage}%`,
        chineseIssue,
        errors: validation.errors || [],
        isValid: validation.isValid
      };
    },
  },

  watch: {
    // 선택된 옵션이 변경될 때 데이터 재초기화 및 검색 태그 글자수 업데이트
    selectedOption: {
      handler() {
        this.$nextTick(() => {
          // 데이터 재초기화 후 글자수 업데이트
          this.initializeData().then(() => {
            this.initializeSearchTagsCount();
            this.initializeModelNumberLength();
            this.$forceUpdate();
          });
        });
      },
      immediate: false
    }
  },

  mounted() {
    // console.log('🚀 QuotationEdit mounted 시작');
    // console.log('📦 product:', this.product ? '존재' : '없음');
    // console.log('📋 selectedOption:', this.selectedOption ? this.selectedOption.optionNumber : '없음');

    // 스키마 초기화
    this.initializeSchema();

    // 데이터 초기화
    this.initializeData().then(() => {
      // console.log('✅ 데이터 초기화 완료');
      // console.log('📄 docData.startPage.productName:', this.docData?.startPage?.productName);

      // 비노출속성의 null 값 처리
      this.processUnexposedAttributes();

      // 검색 태그 글자수 초기화 (데이터 초기화 완료 후)
      this.initializeSearchTagsCount();

      // 모델명 글자수 초기화 (데이터 초기화 완료 후)
      this.initializeModelNumberLength();

      // 강제 화면 갱신
      this.$forceUpdate();
    }).catch(error => {
      console.error('❌ 등록 세부정보 초기화 오류:', error);
    });
  },

  beforeDestroy() {
    console.log('🚪 QuotationEdit 컴포넌트 이탈 감지');

    if (this.autoSaveEnabled && this.hasPendingChanges) {
      console.log('⚠️ [자동저장ON] 저장되지 않은 변경사항 발견 - 동기 저장 시도');
      this.savePendingChanges();
    } else if (this.hasPendingChanges) {
      console.log('ℹ️ [자동저장OFF] 이탈 시 자동 저장하지 않습니다. 저장 버튼으로 저장하세요.');
    }
  },

  beforeRouteLeave(to, from, next) { // eslint-disable-line no-unused-vars
    if (this.autoSaveEnabled && this.hasPendingChanges) {
      console.log('🔄 [자동저장ON] 라우터 이탈 전 pending 변경사항 저장...');
      this.savePendingChanges()
        .then(() => {
          console.log('✅ 저장 완료 - 라우터 이동 허용');
          next();
        })
        .catch(async (error) => {
          console.error('❌ 저장 실패:', error);
          if (await this.confirmInPage('저장에 실패했습니다. 그래도 이동하시겠습니까?')) {
            next();
          } else {
            next(false);
          }
        });
    } else {
      if (this.hasPendingChanges) {
        console.log('ℹ️ [자동저장OFF] 이탈 시 자동 저장하지 않습니다. 저장 버튼으로 저장하세요.');
      }
      next();
    }
  },
  methods: {
    // Vuex 액션 매핑
    ...mapActions([
      'updateRocketProduct',
      'setProcessing',
      'updateProductPartial'
    ]),

    // 저장 버튼 핸들러 (sticky 패널)
    async handleSaveButton() {
      if (this.isSaving) return;
      this.isSaving = true;

      console.log('🔘 저장 버튼 클릭됨');

      // ✨ 저장 전에 현재 포커스된 요소에서 blur 강제 발생 (마지막 입력값 반영)
      if (document.activeElement && document.activeElement.blur) {
        console.log('🔄 [저장 전] 포커스된 요소에서 blur 강제 발생:', document.activeElement.tagName);
        document.activeElement.blur();
        // blur 이벤트 처리를 위한 짧은 대기
        await new Promise(resolve => setTimeout(resolve, 50));
      }

      console.log('📊 저장 전 상태:', {
        hasPendingChanges: this.hasPendingChanges,
        pendingChangesCount: Object.keys(this.pendingChanges).length,
        applyToAllOptions: this.applyToAllOptions
      });

      try {
        // 자동 저장 OFF: 항상 버튼으로만 저장
        const ok = await this.savePendingChanges();
        console.log('💾 savePendingChanges 결과:', ok);

        const msg = ok
          ? (this.applyToAllOptions ? '모든 옵션에 저장되었습니다.' : '변경사항이 저장되었습니다.')
          : '저장할 변경사항이 없습니다.';
        const title = ok ? '저장 완료' : '저장';
        const variant = ok ? 'success' : 'warning';
        if (this.$bvToast) {
          this.$bvToast.toast(msg, {
            title,
            variant,
            solid: true,
            toaster: 'b-toaster-bottom-right',
            autoHideDelay: 2000
          });
        }
      } catch (error) {
        console.error('저장 버튼 처리 중 오류:', error);
        if (this.$bvToast) {
          this.$bvToast.toast(`저장 중 오류가 발생했습니다: ${error?.message || error}`, {
            title: '저장 실패',
            variant: 'danger',
            solid: true,
            toaster: 'b-toaster-bottom-right',
            autoHideDelay: 3000
          });
        }
      } finally {
        this.isSaving = false;
      }
    },

    // 직접 필드만 가져오기 (배열, 객체 아닌 필드)
    getDirectFields(obj) {
      if (!obj) return {};

      const result = {};
      for (const key in obj) {
        // 배열이나 객체가 아닌 필드만 포함
        if (obj[key] !== null &&
          typeof obj[key] !== 'object' ||
          (typeof obj[key] === 'object' && obj[key] === null)) {
          result[key] = obj[key];
        }
      }
      return result;
    },



    // 스키마 초기화
    initializeSchema() {
      try {
        if (!this.hasSchema) return;

        // 스키마 데이터 파싱
        const schemaString = this.product.rocketRegInfo.schemaString;
        this.schema = JSON.parse(schemaString);
      } catch (error) {
        console.error('스키마 초기화 중 오류:', error);
        this.schema = null;
      }
    },

    // 데이터 초기화
    async initializeData() {
      try {
        // console.log('🔄 initializeData 시작');

        // 초기화 중 플래그 설정
        this.isInitializing = true;

        // 제품 정보 확인
        if (!this.product) {
          // console.log('❌ product가 없음');
          return;
        }

        // 1. coupangDoc 확인
        const latestCoupangDoc = this.getCurrentCoupangDoc();
        // console.log('📋 latestCoupangDoc:', latestCoupangDoc ? '존재' : '없음');

        // 2. 유효한 coupangDoc이 있는 경우
        if (latestCoupangDoc && latestCoupangDoc.jsonDocument && latestCoupangDoc.jsonDocument[0]) {
          // console.log('✅ coupangDoc에서 데이터 로드');

          // 깊은 복사를 통해 원본 데이터 로드
          const newDocData = this.deepClone(latestCoupangDoc.jsonDocument[0]);


          // 🔧 unexposedAttributes 구조 정규화 (객체 → 배열)
          if (newDocData?.productPage?.unexposedAttributes) {
            const unexposedAttrs = newDocData.productPage.unexposedAttributes;

            if (!Array.isArray(unexposedAttrs) && typeof unexposedAttrs === 'object') {
              // 🔧 원래 스키마 기반으로 완전한 배열 복원
              const restoredArray = this.restoreUnexposedAttributesArray(unexposedAttrs);

              // 배열로 교체
              newDocData.productPage.unexposedAttributes = restoredArray;
            }
          }

          // 데이터 업데이트
          this.docData = newDocData;

          // 누락된 필드 보완
          this.docData = this.ensureRequiredFields(this.docData);

          // 🔧 "해당사항없음" 문자열을 null로 변환
          this.normalizeUnexposedAttributes();

          // 🔧 Vue 반응성 강제 업데이트
          this.$nextTick(() => {
            this.$forceUpdate();
          });

          // console.log('✅ docData 업데이트 완료');
        }
        // 3. coupangDoc이 없는 경우 기본 데이터 생성
        else {
          // console.log('⚠️ coupangDoc이 없어서 기본 데이터 생성');

          const targetOption = this.selectedOption ||
            (this.product.options && this.product.options.length > 0 ?
              this.product.options[0] : null);

          const baseData = quotationService.createBaseQuotationData(this.product, targetOption);
          this.docData = this.convertToJsonDocument(baseData);
          this.docData = this.ensureRequiredFields(this.docData);
        }

        // 초기화 완료
        this.isInitializing = false;
        // console.log('✅ initializeData 완료');

      } catch (error) {
        console.error('❌ 데이터 초기화 중 오류:', error);
        // 오류 발생 시 빈 구조 생성
        this.docData = this.getEmptyJsonDocument();
        this.isInitializing = false;
      }
    },

    deepClone(obj) {
			if (obj === null || typeof obj !== "object") {
				return obj
			}

			const result = Array.isArray(obj) ? [] : {}

			for (let key of Object.keys(obj)) {
				result[key] = this.deepClone(obj[key])
			}

			return result
		},

    // "해당사항없음" 문자열을 null로 변환
    normalizeUnexposedAttributes() {
      if (this.docData?.productPage?.unexposedAttributes &&
          Array.isArray(this.docData.productPage.unexposedAttributes)) {

        let changedCount = 0;
        this.docData.productPage.unexposedAttributes.forEach(attr => {
          if (attr.attributeValue === '해당사항없음') {
            console.log(`🔄 [데이터 정규화] "${attr.attributeName}": "해당사항없음" → null`);
            attr.attributeValue = null;
            changedCount++;
          }
        });

        if (changedCount > 0) {
          console.log(`✅ [데이터 정규화] ${changedCount}개 비노출속성을 null로 변환 완료`);
        }
      }
    },

    // pendingChanges와 현재 데이터에서 "해당사항없음" 문자열을 null로 변환
    normalizeUnexposedAttributesInPendingChanges() {
      // 1. 현재 docData 정규화
      this.normalizeUnexposedAttributes();

      // 2. pendingChanges에서 비노출속성 관련 변경사항 정규화
      let normalizedCount = 0;
      Object.keys(this.pendingChanges).forEach(fieldPath => {
        if (fieldPath.includes('unexposedAttributes')) {
          const value = this.pendingChanges[fieldPath];

          // 배열 전체가 저장되는 경우
          if (Array.isArray(value)) {
            let arrayChanged = false;
            value.forEach(attr => {
              if (attr.attributeValue === '해당사항없음') {
                console.log(`🔄 [pendingChanges 정규화] "${attr.attributeName}": "해당사항없음" → null`);
                attr.attributeValue = null;
                arrayChanged = true;
                normalizedCount++;
              }
            });
            if (arrayChanged) {
              this.pendingChanges[fieldPath] = value; // 업데이트된 배열로 교체
            }
          }
          // 개별 attributeValue가 저장되는 경우
          else if (value === '해당사항없음') {
            console.log(`🔄 [pendingChanges 정규화] ${fieldPath}: "해당사항없음" → null`);
            this.pendingChanges[fieldPath] = null;
            normalizedCount++;
          }
        }
      });

      if (normalizedCount > 0) {
        console.log(`✅ [pendingChanges 정규화] ${normalizedCount}개 비노출속성을 null로 변환 완료`);
      }
    },

    // coupangDoc 직접 계산 (최신 정보 보장을 위해 computed 속성 직접 접근 피함)
    getCurrentCoupangDoc() {
      let result = null;

      // 상품의 coupangDoc 확인
      if (this.product && this.product.coupangDoc) {
        // 선택된 옵션이 있는 경우 해당 옵션 인덱스의 jsonDocument 항목 사용
        if (this.selectedOption && this.product.options) {
          // 선택된 옵션의 인덱스 찾기
          const optionIndex = this.product.options.findIndex(opt =>
            opt.optionNumber === this.selectedOption.optionNumber);

          // 해당 인덱스의 jsonDocument 항목이 있는지 확인
          if (optionIndex >= 0 &&
              this.product.coupangDoc.jsonDocument &&
              this.product.coupangDoc.jsonDocument.length > optionIndex) {

            // 깊은 복사를 통해 새 coupangDoc 객체 생성
            result = {
              ...this.product.coupangDoc,
              // jsonDocument 배열에서 해당 옵션 인덱스의 항목만 포함
              jsonDocument: [this.product.coupangDoc.jsonDocument[optionIndex]]
            };
          } else {
            // 해당 인덱스의 항목이 없으면 첫 번째 항목 사용
            result = this.product.coupangDoc;
          }
        } else {
          // 선택된 옵션이 없으면 첫 번째 jsonDocument 항목 사용
          result = this.product.coupangDoc;
        }
      }

      // 이전 방식 호환성 유지 (옵션 레벨의 coupangDoc이 있는 경우)
      if (!result && this.selectedOption && this.selectedOption.coupangDoc) {
        result = this.selectedOption.coupangDoc;
      }

      return result;
    },

    // 필드 타이틀 가져오기
    getFieldTitle(section, field) {
      if (!this.schema) return this.getDefaultTitle(section, field);

      try {
        // 섹션 찾기
        const sectionSchema = this.schema.properties[section];
        if (!sectionSchema || !sectionSchema.properties) {
          return this.getDefaultTitle(section, field);
        }

        // 직접 필드인 경우
        if (sectionSchema.properties[field]) {
          return sectionSchema.properties[field].title || this.getDefaultTitle(section, field);
        }

        // 중첩 필드인 경우
        // section.field 형식 찾기 (예: productPage.commonAttributes)
        const nestedParts = field.split('.');
        if (nestedParts.length > 1) {
          let current = sectionSchema.properties;

          for (let i = 0; i < nestedParts.length; i++) {
            if (!current[nestedParts[i]] || !current[nestedParts[i]].properties) {
              return this.getDefaultTitle(section, field);
            }
            current = current[nestedParts[i]].properties;
          }

          return current.title || this.getDefaultTitle(section, field);
        }

        return this.getDefaultTitle(section, field);
      } catch (error) {
        console.error('필드 타이틀 가져오기 오류:', error);
        return this.getDefaultTitle(section, field);
      }
    },

    // 기본 타이틀 가져오기
    getDefaultTitle(section, field) {
      const defaultTitles = {
        'startPage.productName': '상품명',
        'startPage.categoryPath': '카테고리 경로',
        'productPage.modelNumber': '모델 번호',
        'productPage.brand': '브랜드',
        'productPage.manufacturer': '제조사',
        'productPage.businessType': '거래 유형',
        'productPage.taxationSchema': '과세 여부',
        'productPage.importType': '수입 유형',
        'productPage.searchTags': '검색 태그',
        'productPage.commonAttributes.purchasePrice': '공급가',
        'productPage.commonAttributes.coupangSalePrice': '판매가',
        'productPage.commonAttributes.msrp': '권장소비자가격',
        'productPage.commonAttributes.osrp': '공식판매처가격',
        'productPage.commonAttributes.productBarcode': '바코드',
        'legalPage.kcMarkType': 'KC 마크 타입',
        'logisticsPage.totalSKUsInBox': '박스당 입수량',
        'logisticsPage.daysToExpiration': '유통기한(일)',
        'logisticsPage.specialHandlingReason': '특별 취급 사유',
        'logisticsPage.skuUnitBoxWeight': '상품 박스 무게(g)',
        'logisticsPage.skuUnitBoxDimension': '상품 박스 크기(mm)',
        'logisticsPage.fashionSeason': '시즌',
        'imagePage.images.mainImage': '대표 이미지',
        'imagePage.images.sizeChartImage': '사이즈표 이미지',
        'imagePage.images.additionalImage': '추가 이미지',
        'imagePage.details.detailedImage': '상세 이미지',
        'imagePage.details.htmlProductDetailContent': 'HTML 상세 내용',
        'imagePage.details.altText': '대체 텍스트'
      };

      const key = `${section}.${field}`;
      return defaultTitles[key] || field;
    },

    // 필드 설명 가져오기
    getFieldDescription(section, field) {
      if (!this.schema) return '';

      try {
        // 섹션 찾기
        const sectionSchema = this.schema.properties[section];
        if (!sectionSchema || !sectionSchema.properties) {
          return '';
        }

        // 직접 필드인 경우
        if (sectionSchema.properties[field]) {
          return sectionSchema.properties[field].description || '';
        }

        // 중첩 필드인 경우
        // section.field 형식 찾기 (예: productPage.commonAttributes)
        const nestedParts = field.split('.');
        if (nestedParts.length > 1) {
          let current = sectionSchema.properties;

          for (let i = 0; i < nestedParts.length; i++) {
            if (!current[nestedParts[i]] || !current[nestedParts[i]].properties) {
              return '';
            }
            current = current[nestedParts[i]].properties;
          }

          return current.description || '';
        }

        return '';
      } catch (error) {
        console.error('필드 설명 가져오기 오류:', error);
        return '';
      }
    },

    // 필드에 옵션이 있는지 확인
    hasFieldOptions(section, field) {
      if (!this.schema) return false;

      try {
        // 섹션 찾기
        const sectionSchema = this.schema.properties[section];
        if (!sectionSchema || !sectionSchema.properties) {
          return false;
        }

        // 직접 필드인 경우
        if (sectionSchema.properties[field]) {
          return !!(sectionSchema.properties[field].enum ||
            sectionSchema.properties[field].dropdown);
        }

        return false;
      } catch (error) {
        console.error('필드 옵션 확인 오류:', error);
        return false;
      }
    },

    // 필드 옵션 가져오기
    getFieldOptions(section, field) {
      if (!this.schema) return this.getDefaultOptions(section, field);

      try {
        // 섹션 찾기
        const sectionSchema = this.schema.properties[section];
        if (!sectionSchema || !sectionSchema.properties) {
          return this.getDefaultOptions(section, field);
        }

        // 직접 필드인 경우
        if (sectionSchema.properties[field]) {
          return sectionSchema.properties[field].enum ||
            sectionSchema.properties[field].dropdown ||
            this.getDefaultOptions(section, field);
        }

        return this.getDefaultOptions(section, field);
      } catch (error) {
        console.error('필드 옵션 가져오기 오류:', error);
        return this.getDefaultOptions(section, field);
      }
    },

    // 기본 옵션 가져오기
    getDefaultOptions(section, field) {
      const defaultOptions = {
      };

      const key = `${section}.${field}`;
      return defaultOptions[key] || [];
    },

    /**
     * 노출 속성의 드롭다운 옵션을 가져옵니다.
     * @param {string} attributeName - 속성 이름
     * @returns {Array} 드롭다운 옵션 배열
     */
    getExposedAttributeOptions(attributeName) {
      if (!this.schema) return [];

      try {
        return this.getAttributeOptions('exposedAttributes', attributeName);
      } catch (error) {
        console.error(`노출 속성 옵션 가져오기 오류 (${attributeName}):`, error);
        return [];
      }
    },

    /**
     * 비노출 속성의 드롭다운 옵션을 가져옵니다.
     * @param {string} attributeName - 속성 이름
     * @returns {Array} 드롭다운 옵션 배열
     */
    getUnexposedAttributeOptions(attributeName) {
      if (!this.schema) return [];

      try {
        const options = this.getAttributeOptions('unexposedAttributes', attributeName);
        return options;
      } catch (error) {
        console.error(`비노출 속성 옵션 가져오기 오류 (${attributeName}):`, error);
        return [];
      }
    },

    /**
     * 상품 고시 항목의 드롭다운 옵션을 가져옵니다.
     * @param {string} noticeItemName - 고시 항목 이름
     * @returns {Array} 드롭다운 옵션 배열
     */
    getNoticeItemOptions(noticeItemName) {
      if (!this.schema) return [];

      try {
        return this.getAttributeOptions('notices', noticeItemName);
      } catch (error) {
        console.error(`고시 항목 옵션 가져오기 오류 (${noticeItemName}):`, error);
        return [];
      }
    },

    /**
     * 속성 타입별 드롭다운 옵션을 가져옵니다.
     * @param {string} type - 속성 타입 (exposedAttributes, unexposedAttributes, notices)
     * @param {string} name - 속성/항목 이름
     * @returns {Array} 드롭다운 옵션 배열
     */
    getAttributeOptions(type, name) {
      if (!this.schema) return [];

      try {
        // 섹션 및 속성 필드 결정
        let section = '';
        let nameField = '';
        let valueField = '';

        if (type === 'exposedAttributes') {
          section = 'productPage.commonAttributes.exposedAttributes';
          nameField = 'attributeName';
          valueField = 'attributeValue';
        } else if (type === 'unexposedAttributes') {
          section = 'productPage.unexposedAttributes';
          nameField = 'attributeName';
          valueField = 'attributeValue';
        } else if (type === 'notices') {
          section = 'legalPage.notices';
          nameField = 'noticeItemName';
          valueField = 'noticeItemValue';
        } else {
          return [];
        }

        // 스키마에서 해당 섹션 가져오기
        const sectionParts = section.split('.');
        let currentSchema = this.schema;

        for (const part of sectionParts) {
          if (!currentSchema.properties || !currentSchema.properties[part]) {
            return [];
          }
          currentSchema = currentSchema.properties[part];
        }

        // allOf 배열이 있는지 확인
        if (!currentSchema.allOf || !Array.isArray(currentSchema.allOf)) {
          return [];
        }

        // 해당 속성의 항목 찾기
        const item = currentSchema.allOf.find(config =>
          config.contains &&
          config.contains.properties &&
          config.contains.properties[nameField] &&
          config.contains.properties[nameField].enum &&
          config.contains.properties[nameField].enum.includes(name)
        );

        if (!item || !item.contains || !item.contains.properties || !item.contains.properties[valueField]) {
          return [];
        }

        // dropdown 또는 enum 값 반환
        return item.contains.properties[valueField].dropdown ||
               item.contains.properties[valueField].enum || [];
      } catch (error) {
        console.error(`속성 옵션 가져오기 오류 (${type}, ${name}):`, error);
        return [];
      }
    },

    /**
     * 속성 타입별 예시 값을 가져옵니다.
     * @param {string} type - 속성 타입 (exposedAttributes, unexposedAttributes, notices)
     * @param {string} name - 속성/항목 이름
     * @returns {string|null} 예시 값 또는 null
     */
    getAttributeExample(type, name) {
      if (!this.schema) return null;

      try {
        // 섹션 및 속성 필드 결정
        let section = '';
        let nameField = '';
        let valueField = '';

        if (type === 'exposedAttributes') {
          section = 'productPage.commonAttributes.exposedAttributes';
          nameField = 'attributeName';
          valueField = 'attributeValue';
        } else if (type === 'unexposedAttributes') {
          section = 'productPage.unexposedAttributes';
          nameField = 'attributeName';
          valueField = 'attributeValue';
        } else if (type === 'notices') {
          section = 'legalPage.notices';
          nameField = 'noticeItemName';
          valueField = 'noticeItemValue';
        } else {
          return null;
        }

        // 스키마에서 해당 섹션 가져오기
        const sectionParts = section.split('.');
        let currentSchema = this.schema;

        for (const part of sectionParts) {
          if (!currentSchema.properties || !currentSchema.properties[part]) {
            return null;
          }
          currentSchema = currentSchema.properties[part];
        }

        // allOf 배열이 있는지 확인
        if (!currentSchema.allOf || !Array.isArray(currentSchema.allOf)) {
          return null;
        }

        // 해당 속성의 항목 찾기
        const item = currentSchema.allOf.find(config =>
          config.contains &&
          config.contains.properties &&
          config.contains.properties[nameField] &&
          config.contains.properties[nameField].enum &&
          config.contains.properties[nameField].enum.includes(name)
        );

        if (!item || !item.contains || !item.contains.properties || !item.contains.properties[valueField]) {
          return null;
        }

        // example 값 반환
        const valueSchema = item.contains.properties[valueField];

        // 예시 값 찾기 (여러 가능한 필드 이름 확인)
        const example = valueSchema.example ||
                        valueSchema.examples?.[0] ||
                        valueSchema.default ||
                        (valueSchema.enum && valueSchema.enum.length > 0 ? valueSchema.enum[0] : null);

        return example;
      } catch (error) {
        console.error(`속성 예시 가져오기 오류 (${type}, ${name}):`, error);
        return null;
      }
    },

    // 필수 필드인지 확인
    isRequiredField(section, field) {
      if (!this.schema) return false;

      try {
        // 섹션 찾기
        const sectionSchema = this.schema.properties[section];
        if (!sectionSchema) {
          return false;
        }

        // required 배열이 있는지 확인
        if (!sectionSchema.required || !Array.isArray(sectionSchema.required)) {
          return false;
        }

        return sectionSchema.required.includes(field);
      } catch (error) {
        console.error('필수 필드 확인 오류:', error);
        return false;
      }
    },

    // 스키마에서 필드가 허용되는지 확인
    isFieldAllowedInSchema(section, field) {
      if (!this.schema) return true; // 스키마가 없으면 모든 필드 허용

      try {
        // 섹션 찾기
        const sectionSchema = this.schema.properties[section];
        if (!sectionSchema || !sectionSchema.properties) {
          return true; // 섹션이 없으면 허용
        }

        // 해당 필드가 스키마에 정의되어 있는지 확인
        return Object.prototype.hasOwnProperty.call(sectionSchema.properties, field);
      } catch (error) {
        console.error('스키마 필드 확인 오류:', error);
        return true; // 오류 시 허용
      }
    },

    // baseData를 jsonDocument 형식으로 변환
    convertToJsonDocument(baseData) {
      console.log('==== convertToJsonDocument 실행 ====');
      console.log('현재 this.product.seo:', this.product.seo);

      // 상품 이름 설정
      let productName = '';
      if (this.product.seo && this.product.seo.productName) {
        productName = this.product.seo.productName;
        console.log('[견적서] SEO에서 상품명 가져옴:', productName);
      } else if (this.product.productName) {
        productName = this.product.productName;
        console.log('[견적서] 원본 상품명 사용:', productName);
      } else if (this.product.productNameTrans) {
        productName = this.product.productNameTrans;
        console.log('[견적서] 번역된 상품명 사용:', productName);
      }

      // 선택된 옵션이 있으면 옵션명 추가
      if (this.selectedOption && this.selectedOption.korName) {
        let originalName = productName;
        productName = `${productName}, ${this.selectedOption.korName}`;
        console.log('[견적서] 옵션명 추가:', originalName, '->', productName);
      }

      // 카테고리 경로 설정
      const categoryPath = this.product.rocketRegInfo?.categoryPath || '';

      // jsonDocument 구조
      return {
        startPage: {
          productName: productName,
          categoryPath: categoryPath
        },
        productPage: {
          // 모델번호 50자 제한 적용
          modelNumber: this.limitModelNumber(baseData.modelNumber || productName),
          brand: baseData.brand || '',
          commonAttributes: {
            exposedAttributes: baseData.exposedAttributes || [],
            purchasePrice: baseData.purchasePrice || 0,
            coupangSalePrice: baseData.salePrice || 0,
            msrp: baseData.msrp || 0,
            productBarcode: baseData.barcode || '바코드 없음(쿠팡 바코드 생성 요청)'
          },
          manufacturer: baseData.manufacturer || '',
          businessType: baseData.businessType || '제조사',
          taxationSchema: baseData.taxationType || '과세',
          importType: baseData.importType || '수입대상아님',
          searchTags: this.product.seo?.keywords ? this.product.seo.keywords.join(', ') : '',
          unexposedAttributes: baseData.unexposedAttributes || []
        },
        imagePage: {
          images: {
            mainImage: this.getMainImageUrl(),
            additionalImage: this.getAdditionalImageUrls(),
            labelImages: [
              {
                labelImageType: '제품 한글 표시사항 라벨 또는 도안 이미지',
                labelImageFiles: this.product.koreanLabelImage || ''
              }
            ]
          },
          details: {
            detailedImage: this.getDetailImageUrls(),
            htmlProductDetailContent: this.product.detailHtml || '',
            altText: this.product.seo?.metaDescription || '상품 상세 설명'
          }
        },
        legalPage: {
          kcMarkType: baseData.kcMarkType || '해당사항없음',
          certificates: baseData.certificates || [],
          adCert: '',
          notices: baseData.productNotices ? baseData.productNotices.map(notice => ({
            noticeItemName: notice.name,
            noticeItemValue: notice.value
          })) : []
        },
        logisticsPage: {
          // 실제 baseData에 있는 물류 정보만 포함 (기본값 강제 설정 제거)
          ...(baseData.totalSkusInBox !== undefined && { totalSKUsInBox: baseData.totalSkusInBox }),
          ...(baseData.daysToExpiration !== undefined && { daysToExpiration: baseData.daysToExpiration }),
          ...(baseData.specialHandlingReason && { specialHandlingReason: baseData.specialHandlingReason }),
          ...(baseData.skuUnitBoxWeight && { skuUnitBoxWeight: baseData.skuUnitBoxWeight }),
          ...(baseData.skuUnitBoxDimension && { skuUnitBoxDimension: baseData.skuUnitBoxDimension }),
          ...(baseData.fashionSeason && { fashionSeason: baseData.fashionSeason })
        },
        sourcingPage: {
          sourcingChannelType: '',
          sourcingChannelId: ''
        }
      };
    },

    // 필요한 필드가 모두 있는지 확인하고 누락된 경우 기본값 설정 (스키마 기반)
    ensureRequiredFields(data) {
      // 기본 빈 구조
      const emptyDoc = this.getEmptyJsonDocument();

      // 각 섹션이 없는 경우 빈 섹션 추가 (logisticsPage 제외)
      for (const sectionKey in emptyDoc) {
        if (sectionKey === 'logisticsPage') {
          // logisticsPage는 실제 데이터가 있을 때만 유지, 없으면 빈 객체
          if (!data[sectionKey]) {
            data[sectionKey] = {};
          }
          // 단품 포장 크기·무게는 필드가 없어도 입력칸이 보이도록 빈 값(null)으로만 키를 만든다 (PR261018-89E028)
          ['skuUnitBoxDimension', 'skuUnitBoxWeight'].forEach(key => {
            if (!(key in data[sectionKey])) this.$set(data[sectionKey], key, null);
          });
          // 기존 물류 데이터가 있으면 그대로 유지, 하드코딩 추가 안함
          continue;
        }

        if (!data[sectionKey]) {
          data[sectionKey] = emptyDoc[sectionKey];
        } else {
          // 중첩 객체 처리
          for (const subKey in emptyDoc[sectionKey]) {
            if (!data[sectionKey][subKey]) {
              data[sectionKey][subKey] = emptyDoc[sectionKey][subKey];
            } else if (typeof emptyDoc[sectionKey][subKey] === 'object' && !Array.isArray(emptyDoc[sectionKey][subKey])) {
              // 중첩된 객체의 누락된 필드 처리
              for (const nestedKey in emptyDoc[sectionKey][subKey]) {
                if (!data[sectionKey][subKey][nestedKey]) {
                  data[sectionKey][subKey][nestedKey] = emptyDoc[sectionKey][subKey][nestedKey];
                }
              }
            }
          }
        }
      }

      // 스키마 기반으로 msrp/osrp 필드 동적 추가
      this.ensureSchemaBasedPriceFields(data);

      // 비노출속성(unexposedAttributes)의 null 값 처리
      if (data.productPage && data.productPage.unexposedAttributes) {
        // null 값이 있는지 확인하고 로그 출력
        const hasNullValues = data.productPage.unexposedAttributes.some(attr => attr.attributeValue === null);
        if (hasNullValues) {
          console.log('비노출속성에 null 값이 있습니다. 표시를 위해 처리합니다.');
        }
      }

      return data;
    },

    // 스키마 기반으로 msrp/osrp 필드 동적 처리 (실제 데이터에 있는 필드만 유지)
    ensureSchemaBasedPriceFields(data) {
      if (!this.schema || !data.productPage || !data.productPage.commonAttributes) {
        return;
      }

      try {
        // productPage.commonAttributes 스키마 확인
        const commonAttributesSchema = this.schema.properties?.productPage?.properties?.commonAttributes;
        if (!commonAttributesSchema || !commonAttributesSchema.properties) {
          return;
        }

        // 스키마에 없는 msrp/osrp 필드 제거 (실제 데이터에 있어도)
        if (!commonAttributesSchema.properties.msrp && Object.prototype.hasOwnProperty.call(data.productPage.commonAttributes, 'msrp')) {
          delete data.productPage.commonAttributes.msrp;
          console.log('스키마에 없는 msrp 필드 제거');
        }

        if (!commonAttributesSchema.properties.osrp && Object.prototype.hasOwnProperty.call(data.productPage.commonAttributes, 'osrp')) {
          delete data.productPage.commonAttributes.osrp;
          console.log('스키마에 없는 osrp 필드 제거');
        }

        // 실제 데이터에 있는 필드만 유지하고, 없는 필드는 추가하지 않음
        // 이렇게 하면 pricingService에서 설정한 값이 그대로 유지됨
        console.log('스키마 기반 가격 필드 처리 완료 - 실제 데이터 기준으로 유지');
      } catch (error) {
        console.error('스키마 기반 가격 필드 처리 중 오류:', error);
      }
    },

    // 빈 jsonDocument 구조 생성 (스키마 기반으로 msrp/osrp 동적 추가)
    getEmptyJsonDocument() {
      return {
        startPage: {
          productName: '',
          categoryPath: ''
        },
        productPage: {
          modelNumber: '',
          brand: '',
          commonAttributes: {
            exposedAttributes: [],
            purchasePrice: 0,
            coupangSalePrice: 0,
            productBarcode: '바코드 없음(쿠팡 바코드 생성 요청)'
            // msrp/osrp는 ensureSchemaBasedPriceFields에서 동적으로 추가됨
          },
          manufacturer: '',
          businessType: '제조사',
          taxationSchema: '과세',
          importType: '수입대상아님',
          searchTags: '',
          unexposedAttributes: []
        },
        imagePage: {
          images: {
            mainImage: '',
            additionalImage: '',
            labelImages: []
          },
          details: {
            detailedImage: '',
            htmlProductDetailContent: '',
            altText: ''
          }
        },
        legalPage: {
          kcMarkType: '해당사항없음',
          certificates: [],
          adCert: '해당사항없음',
          notices: []
        },
        logisticsPage: {
          // 빈 구조 - AI가 생성한 데이터만 표시
        },
        sourcingPage: {
          sourcingChannelType: '',
          sourcingChannelId: ''
        }
      };
    },

    // 메인 이미지 URL 가져오기
    getMainImageUrl() {
      // 선택된 옵션의 이미지
      if (this.selectedOption && this.selectedOption.mainImageStatus) {
        return this.selectedOption.mainImageStatus.processedImage ||
          this.selectedOption.mainImageStatus.image || '';
      }

      // 첫 번째 옵션의 이미지
      if (this.product.options && this.product.options.length > 0) {
        const firstOption = this.product.options[0];
        if (firstOption.mainImageStatus) {
          return firstOption.mainImageStatus.processedImage ||
            firstOption.mainImageStatus.image || '';
        }
      }

      return '';
    },

    // 추가 이미지 URL 가져오기
    getAdditionalImageUrls() {
      if (this.product.additionalImages && this.product.additionalImages.length > 0) {
        return this.product.additionalImages.map(img =>
          typeof img === 'object' ? (img.processedUrl || img.url || "") : img
        ).join(',');
      }

      return '';
    },

    // 상세 이미지 URL 가져오기
    getDetailImageUrls() {
      if (this.product.detailImages && this.product.detailImages.length > 0) {
        return this.product.detailImages.map(img =>
          typeof img === 'object' ? (img.processedUrl || img.url || "") : img
        ).join(',');
      }

      return '';
    },

    // docData를 quotationService가 처리할 수 있는 형태로 변환
    convertDocToQuotationData() {
      // 비노출 속성 처리 - 값이 null, 빈 문자열, '해당사항없음'인 경우 null로 변환
      const processedUnexposedAttributes = this.docData.productPage.unexposedAttributes.map(attr => {
        const attributeValue = attr.attributeValue === null || attr.attributeValue === '' ||
                               attr.attributeValue === '해당사항없음' ? null : attr.attributeValue;

        // 값이 변경되었으면 로그 출력
        if (attr.attributeValue !== attributeValue) {
          console.log(`비노출속성 "${attr.attributeName}"의 값 "${attr.attributeValue}"을(를) null로 변환합니다.`);
        }

        return {
          ...attr,
          attributeValue: attributeValue
        };
      });

      // 브랜드 설정 - 브랜드 이름은 콤마 구분된 전체 값 저장
      // product.brand에는 콤마 구분된 전체 값이 저장되어야 하므로 그대로 유지
      const brandValue = this.docData.productPage.brand;
      console.log(`브랜드 값: ${brandValue} (product.brand에 그대로 저장됩니다)`);

      // 검색 태그 로깅
      console.log(`검색 태그 값: "${this.docData.productPage.searchTags || ''}"`);

      // 상품명 로깅
      console.log(`상품명 값: "${this.docData.startPage.productName || ''}"`);

      return {
        // 기본 정보
        productName: this.docData.startPage.productName, // 상품명 추가
        modelNumber: this.docData.productPage.modelNumber,
        brand: brandValue, // 브랜드는 콤마 구분된 전체 값 사용 (product.brand용)
        manufacturer: this.docData.productPage.manufacturer,
        businessType: this.docData.productPage.businessType,
        taxationType: this.docData.productPage.taxationSchema,
        importType: this.docData.productPage.importType,

        // 검색 태그 추가
        searchTags: this.docData.productPage.searchTags,

        // 가격 정보 (스키마에 있는 필드만 포함)
        purchasePrice: this.docData.productPage.commonAttributes.purchasePrice,
        salePrice: this.docData.productPage.commonAttributes.coupangSalePrice,
        ...(this.isFieldAllowedInSchema('productPage.commonAttributes', 'msrp') &&
            Object.prototype.hasOwnProperty.call(this.docData.productPage.commonAttributes, 'msrp') &&
            { msrp: this.docData.productPage.commonAttributes.msrp }),
        ...(this.isFieldAllowedInSchema('productPage.commonAttributes', 'osrp') &&
            Object.prototype.hasOwnProperty.call(this.docData.productPage.commonAttributes, 'osrp') &&
            { osrp: this.docData.productPage.commonAttributes.osrp }),
        barcode: this.docData.productPage.commonAttributes.productBarcode,

        // 속성 정보
        exposedAttributes: this.docData.productPage.commonAttributes.exposedAttributes,
        unexposedAttributes: processedUnexposedAttributes,

        // 법적 정보
        kcMarkType: this.docData.legalPage.kcMarkType,
        certificates: this.docData.legalPage.certificates.map(cert => ({
          name: cert.certificateName,
          value: cert.certificateValue
        })),
        productNotices: this.docData.legalPage.notices.map(notice => ({
          name: notice.noticeItemName,
          value: notice.noticeItemValue
        })),

        // 물류 정보 (실제 데이터가 있을 때만 포함)
        ...(this.docData.logisticsPage.totalSKUsInBox !== undefined && { totalSkusInBox: this.docData.logisticsPage.totalSKUsInBox }),
        ...(this.docData.logisticsPage.daysToExpiration !== undefined && { daysToExpiration: this.docData.logisticsPage.daysToExpiration }),
        ...(this.docData.logisticsPage.specialHandlingReason && { specialHandlingReason: this.docData.logisticsPage.specialHandlingReason }),
        ...(this.docData.logisticsPage.skuUnitBoxWeight && { skuUnitBoxWeight: this.docData.logisticsPage.skuUnitBoxWeight }),
        ...(this.docData.logisticsPage.skuUnitBoxDimension && { skuUnitBoxDimension: this.docData.logisticsPage.skuUnitBoxDimension }),
        ...(this.docData.logisticsPage.fashionSeason && { fashionSeason: this.docData.logisticsPage.fashionSeason })
      };
    },

    // AI를 사용한 견적서 생성
    async processQuotationWithAI() {
      try {
        console.log('===== AI 처리 시작 =====');

        if (!this.product || !this.product.registrationId) {
          this.$bvToast.toast('선택된 상품이 없습니다.', {
            title: '알림',
            variant: 'warning',
            solid: true,
            toaster: 'b-toaster-bottom-right'
          });
          return;
        }

        // quotationService.process 호출 (applyToAllOptions 옵션 전달)
        const runProcess = () => quotationService.process(this.product, { applyToAllOptions: this.applyToAllOptions });
        // 사람이 고친 노출속성 값은 견적 갱신이 늘 지킨다 — «AI 로 전부 다시» 만 확인 한 번 뒤 덮는다(보고 3320E1).
        // 기록 비우기는 다시 만들기가 끝난 뒤에만 저장한다 — 실패면 사람 값 그대로(총괄 조건 1)
        // 셈·비우기는 견적서 노출속성 기록만 — SEO 옵션명(@korName) 기록은 견적과 무관해 남긴다(조건 1 2차)
        const editCount = quotationEditCount(this.product.quotationUserEdits);
        let result;
        if (editCount) {
          if (!(await this.confirmInPage(`견적서에서 직접 고친 값 ${editCount}개를 AI 값으로 덮어씁니다. 계속할까요?`))) return;
          result = await runDiscardingUserEdits(this.product, runProcess,
            edits => this.updateProductPartial({ productId: this.product._id, updateFields: { quotationUserEdits: edits } }));
        } else {
          result = await runProcess();
        }

        console.log('AI 처리 결과:', result);

        // 성공 메시지 표시 (처리 상태가 processing이면 이미 서비스 내에서 알림이 표시됨)
        if (result.status === 'completed') {
          this.$bvToast.toast(result.message || '등록 세부정보가 성공적으로 생성되었습니다.', {
            title: '성공',
            variant: 'success',
            solid: true,
            toaster: 'b-toaster-bottom-right'
          });

          // 데이터 새로고침
          this.initializeData();
        }
      } catch (error) {
        console.error('AI 처리 중 오류:', error);

        // 에러 메시지 표시
        this.$bvToast.toast(`AI 처리 중 오류가 발생했습니다: ${error.message}`, {
          title: '오류',
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-bottom-right'
        });

        // 로딩 상태 해제 (에러 발생 시 로딩 상태가 유지될 수 있음)
        this.setProcessing({ isProcessing: false });
      }
    },

    // saveQuotation 함수 제거됨 - 자동 저장 + 검증으로 대체

    // 견적서 초기화
    resetQuotation() {
      this.$bvModal.msgBoxConfirm('등록 세부정보를 초기화하시겠습니까?', {
        title: '확인',
        okVariant: 'danger',
        okTitle: '초기화',
        cancelTitle: '취소',
        hideHeaderClose: false,
        centered: true
      })
        .then(confirmed => {
          if (confirmed) {
            this.initializeData();

            this.$bvToast.toast('등록 세부정보가 초기화되었습니다.', {
              title: '알림',
              variant: 'info',
              solid: true,
              toaster: 'b-toaster-bottom-right'
            });
          }
        });
    },



    /**
     * 모델 번호 길이 제한을 직접 강제하는 메서드
     * 입력 시 즉시 50자로 제한합니다
     */
    enforceModelNumberLimit() {
      if (this.docData.productPage && this.docData.productPage.modelNumber !== undefined) {
        const modelNumber = this.docData.productPage.modelNumber;

        // 글자수 업데이트
        this.modelNumberLength = modelNumber ? modelNumber.length : 0;

        if (modelNumber && modelNumber.length > 50) {
          console.log(`모델번호 길이(${modelNumber.length})가 50자를 초과합니다. 50자로 즉시 자릅니다.`);
          this.$set(this.docData.productPage, 'modelNumber', modelNumber.substring(0, 50));
          this.modelNumberLength = 50;
          console.log(`최종 모델번호 길이: ${this.docData.productPage.modelNumber.length}`);
        }

        console.log('🔤 모델명 글자수 업데이트:', this.modelNumberLength);
      } else {
        this.modelNumberLength = 0;
      }
    },

    /**
     * 모델번호를 50자로 제한하는 유틸리티 함수
     * @param {string} modelNumber - 제한할 모델번호
     * @returns {string} 50자 이내로 제한된 모델번호
     */
    limitModelNumber(modelNumber) {
      if (!modelNumber) return '';

      if (modelNumber.length > 50) {
        console.log(`[limitModelNumber] 모델번호 길이(${modelNumber.length})가 50자를 초과합니다. 50자로 자릅니다.`);
        const limitedValue = modelNumber.substring(0, 50);
        console.log(`[limitModelNumber] 최종 모델번호 길이: ${limitedValue.length}, 값: ${limitedValue}`);
        return limitedValue;
      }

      return modelNumber;
    },

    /**
     * 비노출속성의 null 값을 처리하는 메서드
     * Vue의 반응성 문제로 직접 null 값을 처리
     */
    processUnexposedAttributes() {
      if (!this.docData.productPage || !this.docData.productPage.unexposedAttributes) {
        return;
      }

      // 🔧 세트여부 값 보존: null이 아닌 값은 건드리지 않음
      const nullAttributes = this.docData.productPage.unexposedAttributes
        .filter(attr => attr.attributeValue === null)
        .map(attr => attr.attributeName);

      if (nullAttributes.length > 0) {
        console.log('비노출속성 null 값 처리 중:', nullAttributes.join(', '));

        // 🔧 null 값만 처리하고, 기존 값은 보존
        this.docData.productPage.unexposedAttributes.forEach(attr => {
          if (attr.attributeValue === null) {
            console.log(`비노출속성 null 값 처리 중: ${attr.attributeName}`);

            // 일시적으로 빈 문자열로 변경
            this.$set(attr, 'attributeValue', '');

            // 다음 틱에서 다시 null로 변경
            this.$nextTick(() => {
              this.$set(attr, 'attributeValue', null);
              console.log(`속성 "${attr.attributeName}"의 값을 null로 설정 완료`);
            });
          } else {
            // 🎯 세트여부 추적: 기존 값 보존 확인
            if (attr.attributeName === '세트여부') {
              console.log(`🎯 [세트여부 추적] processUnexposedAttributes에서 기존 값 보존: ${attr.attributeValue}`);
            }
          }
        });
      }
    },

    // 검색 태그 변경 이벤트 핸들러
    onSearchTagsChange() {
      if (this.docData && this.docData.productPage && this.docData.productPage.searchTags !== undefined) {
        this.searchTagsCount = this.docData.productPage.searchTags.length;
        console.log('🔤 검색태그 변경됨, 글자수:', this.searchTagsCount);
      } else {
        this.searchTagsCount = 0;
        console.log('🔤 검색태그 데이터 없음, 글자수 0으로 설정');
      }
    },

    // 검색 태그 글자수 초기화
    initializeSearchTagsCount() {
      if (this.docData && this.docData.productPage && this.docData.productPage.searchTags) {
        this.searchTagsCount = this.docData.productPage.searchTags.length;
        console.log('🔤 검색태그 글자수 초기화:', this.searchTagsCount, '글자');
      } else {
        this.searchTagsCount = 0;
        console.log('🔤 검색태그 데이터 없음, 글자수 0으로 설정');
      }
    },

    // 모델명 글자수 초기화
    initializeModelNumberLength() {
      if (this.docData && this.docData.productPage && this.docData.productPage.modelNumber) {
        this.modelNumberLength = this.docData.productPage.modelNumber.length;
        console.log('🔤 모델명 글자수 초기화:', this.modelNumberLength, '글자');
      } else {
        this.modelNumberLength = 0;
        console.log('🔤 모델명 데이터 없음, 글자수 0으로 설정');
      }
    },

    // 가격 필드 변경 이벤트 핸들러
    onPriceFieldChange(fieldName) {
      const value = this.docData.productPage.commonAttributes[fieldName];
      console.log(`가격 필드 변경: ${fieldName} = ${value}`);

      // 음수 값 방지
      if (value < 0) {
        this.docData.productPage.commonAttributes[fieldName] = 0;
      }

      // 필요시 다른 가격 필드 자동 계산 로직 추가 가능
      // 예: 공급가 변경 시 마진율에 따른 판매가 자동 계산 등
    },

    // coupangDoc 필드를 점 표기법으로 업데이트 필드에 추가
    addDocumentFieldsToUpdateFields(updateFields, docIndex, quotationData) {
      const prefix = `coupangDoc.jsonDocument.${docIndex}`;

      // startPage 필드들
      if (quotationData.productName) {
        updateFields[`${prefix}.startPage.productName`] = quotationData.productName;
      }

      // productPage 필드들
      if (quotationData.modelNumber) {
        updateFields[`${prefix}.productPage.modelNumber`] = quotationData.modelNumber;
      }
      if (quotationData.searchTags) {
        updateFields[`${prefix}.productPage.searchTags`] = quotationData.searchTags;
      }
      if (quotationData.productDescription) {
        updateFields[`${prefix}.productPage.productDescription`] = quotationData.productDescription;
      }

      // commonAttributes 필드들
      if (quotationData.purchasePrice !== undefined) {
        updateFields[`${prefix}.productPage.commonAttributes.purchasePrice`] = quotationData.purchasePrice;
      }
      if (quotationData.coupangSalePrice !== undefined) {
        updateFields[`${prefix}.productPage.commonAttributes.coupangSalePrice`] = quotationData.coupangSalePrice;
      }

      // msrp/osrp 필드 - 스키마에 따라 동적 처리
      if (quotationData.msrp !== undefined && this.isFieldAllowedInSchema('productPage.commonAttributes', 'msrp')) {
        updateFields[`${prefix}.productPage.commonAttributes.msrp`] = quotationData.msrp;
      }
      if (quotationData.osrp !== undefined && this.isFieldAllowedInSchema('productPage.commonAttributes', 'osrp')) {
        updateFields[`${prefix}.productPage.commonAttributes.osrp`] = quotationData.osrp;
      }

      // exposedAttributes 필드들
      if (quotationData.exposedAttributes && Array.isArray(quotationData.exposedAttributes)) {
        quotationData.exposedAttributes.forEach((attr, attrIndex) => {
          if (attr.attributeValue !== undefined) {
            updateFields[`${prefix}.productPage.commonAttributes.exposedAttributes.${attrIndex}.attributeValue`] = attr.attributeValue;
          }
        });
      }

      // unexposedAttributes 필드들
      if (quotationData.unexposedAttributes && Array.isArray(quotationData.unexposedAttributes)) {
        quotationData.unexposedAttributes.forEach((attr, attrIndex) => {
          if (attr.attributeValue !== undefined) {
            updateFields[`${prefix}.productPage.unexposedAttributes.${attrIndex}.attributeValue`] = attr.attributeValue;
          }
        });
      }

      // logisticsPage 필드들
      if (quotationData.totalSkusInBox !== undefined) {
        updateFields[`${prefix}.logisticsPage.totalSKUsInBox`] = quotationData.totalSkusInBox;
      }
      if (quotationData.daysToExpiration !== undefined) {
        updateFields[`${prefix}.logisticsPage.daysToExpiration`] = quotationData.daysToExpiration;
      }
      if (quotationData.specialHandlingReason) {
        updateFields[`${prefix}.logisticsPage.specialHandlingReason`] = quotationData.specialHandlingReason;
      }
      if (quotationData.skuUnitBoxWeight) {
        updateFields[`${prefix}.logisticsPage.skuUnitBoxWeight`] = quotationData.skuUnitBoxWeight;
      }
      if (quotationData.skuUnitBoxDimension) {
        updateFields[`${prefix}.logisticsPage.skuUnitBoxDimension`] = quotationData.skuUnitBoxDimension;
      }
      if (quotationData.fashionSeason) {
        updateFields[`${prefix}.logisticsPage.fashionSeason`] = quotationData.fashionSeason;
      }

      // legalPage 필드들
      if (quotationData.kcMarkType) {
        updateFields[`${prefix}.legalPage.kcMarkType`] = quotationData.kcMarkType;
      }
      if (quotationData.certificates && Array.isArray(quotationData.certificates)) {
        quotationData.certificates.forEach((cert, certIndex) => {
          if (cert.certificateValue !== undefined) {
            updateFields[`${prefix}.legalPage.certificates.${certIndex}.certificateValue`] = cert.certificateValue;
          }
        });
      }
      if (quotationData.productNotices && Array.isArray(quotationData.productNotices)) {
        quotationData.productNotices.forEach((notice, noticeIndex) => {
          if (notice.value !== undefined) {
            updateFields[`${prefix}.legalPage.notices.${noticeIndex}.noticeItemValue`] = notice.value;
          }
        });
      }

      console.log(`📊 문서 ${docIndex}에 대한 필드 추가 완료`);
    },

    // ===== 스위치 변경 이벤트 핸들러 =====

    // 모든 옵션 적용 스위치 변경 이벤트
    onApplyToAllOptionsChange(event) {
      const newValue = event.target.checked;
      const oldValue = !newValue;

      console.log(`🔄 모든 옵션 적용 스위치 변경: ${oldValue} → ${newValue}`);

      if (newValue) {
        // OFF → ON 전환
        this.handleSwitchToOn();
      } else {
        // ON → OFF 전환
        this.handleSwitchToOff();
      }
    },

    // OFF → ON 전환 처리
    async handleSwitchToOn() {
      // 자동 저장 OFF 정책: 모드 전환 시 서버 저장/검증 수행하지 않음
      console.log('📋 OFF → ON: 모드만 전환 (저장은 저장 버튼으로 수행)');
    },

    // ON → OFF 전환 처리
    async handleSwitchToOff() {
      // 자동 저장 OFF 정책: 모드 전환 시 서버 저장/검증 수행하지 않음
      console.log('📋 ON → OFF: 모드만 전환 (저장은 저장 버튼으로 수행)');
    },

    // ===== @blur 기반 자동저장 함수들 =====

    // 상품명을 모든 옵션에 적용 (지연 저장)
    applyProductNameToAllOptions(productName) {
      if (!this.product?.coupangDoc?.jsonDocument) return;

      this.product.coupangDoc.jsonDocument.forEach((_, docIndex) => {
        const fullFieldPath = `coupangDoc.jsonDocument.${docIndex}.startPage.productName`;
        this.pendingChanges[fullFieldPath] = productName;
      });
      this.pendingChanges['updatedAt'] = Date.now();
      this.hasPendingChanges = true;
      console.log('📝 [지연저장] 상품명 모든 옵션 적용(대기):', productName);
    },

    // 일반 필드 blur 이벤트 처리
    onFieldBlur(fieldPath, value) {
      console.log(`🔄 필드 blur: ${fieldPath} = ${value}`);
      console.log(`📊 현재 상태: applyToAllOptions=${this.applyToAllOptions}, hasPendingChanges=${this.hasPendingChanges}`);

      // 로컬 docData는 항상 즉시 업데이트 (UI 반응성)
      const cleanPath = fieldPath.replace('productPage.commonAttributes.', '').replace('productPage.', '');
      this.setNestedValue(this.docData, cleanPath, value);

      if (this.applyToAllOptions) {
        // ON: 즉시 저장 + 스키마 검증
        console.log('🔧 ON 모드: 즉시 저장 + 검증');
        this.applySingleFieldToAllOptionsWithValidation(fieldPath, value);
      } else {
        // OFF: pending에 추가만
        console.log('🔧 OFF 모드: pending에 추가');
        this.addToPending(fieldPath, value);
      }

      console.log(`📊 blur 처리 후: hasPendingChanges=${this.hasPendingChanges}, pendingChanges 개수=${Object.keys(this.pendingChanges).length}`);
    },

    // 속성 blur 이벤트 처리 (exposedAttributes, unexposedAttributes)
    onAttributeBlur(attributeType, index, value) {
      // 🔧 기존 값과 비교하여 변경된 경우에만 처리
      const originalValue = this.getOriginalAttributeValue(attributeType, index);

      if (originalValue === value) {
        console.log(`🔍 값이 변경되지 않음: ${attributeType}Attributes.${index}.attributeValue = ${value} (기존값과 동일)`);
        return; // 값이 변경되지 않았으면 아무것도 하지 않음
      }

      const fieldPath = `${attributeType}Attributes.${index}.attributeValue`;
      console.log(`🔄 속성 blur (값 변경됨): ${fieldPath} = ${originalValue} → ${value}`);

      // 로컬 docData는 항상 즉시 업데이트 (UI 반응성)
      const cleanPath = fieldPath.replace('productPage.commonAttributes.', '').replace('productPage.', '');
      this.setNestedValue(this.docData, cleanPath, value);

      // 비노출 속성은 productPage 바로 아래에 있다(commonAttributes 아님) — 드롭다운 처리와 같은 경로.
      // (예전엔 글자 입력칸도 commonAttributes 밑으로 써서 «모든 옵션 적용» 저장 뒤 값이 되돌아갔다 — PR261017-1FD9C3)
      const fullFieldPath = attributeType === 'unexposed'
        ? `productPage.${fieldPath}`
        : `productPage.commonAttributes.${fieldPath}`;

      if (this.applyToAllOptions) {
        // ON: 즉시 저장 + 스키마 검증
        this.applySingleFieldToAllOptionsWithValidation(fullFieldPath, value);
      } else {
        // OFF: pending에 추가만
        this.addToPending(fullFieldPath, value);
      }
    },

    // dropdown 변경 이벤트 처리
    onDropdownChange(fieldPath, value) {
      console.log(`🔄 dropdown 변경: ${fieldPath} = ${value}`);

      // 로컬 docData는 항상 즉시 업데이트 (UI 반응성)
      const cleanPath = fieldPath.replace('productPage.commonAttributes.', '').replace('productPage.', '');
      this.setNestedValue(this.docData, cleanPath, value);

      if (this.applyToAllOptions) {
        // ON: 즉시 저장 + 스키마 검증
        console.log('ON 모드: 즉시 저장 + 검증');
        this.applySingleFieldToAllOptionsWithValidation(fieldPath, value);
      } else {
        // OFF: pending에 추가만
        console.log('OFF 모드: pending에 추가');
        this.addToPending(fieldPath, value);
      }
    },

    // 속성 dropdown 변경 이벤트 처리
    onAttributeDropdownChange(attributeType, index, value) {
      const fieldPath = `${attributeType}Attributes.${index}.attributeValue`;
      console.log(`🔄 속성 dropdown 변경: ${fieldPath} = ${value}`);

      // 로컬 docData는 항상 즉시 업데이트 (UI 반응성)
      const cleanPath = fieldPath.replace('productPage.commonAttributes.', '').replace('productPage.', '');
      this.setNestedValue(this.docData, cleanPath, value);

      if (this.applyToAllOptions) {
        // ON: 즉시 저장 + 스키마 검증


        // 🔧 속성 타입에 따라 올바른 경로 사용
        let fullFieldPath;
        if (attributeType === 'exposed') {
          fullFieldPath = `productPage.commonAttributes.${fieldPath}`;
        } else if (attributeType === 'unexposed') {
          fullFieldPath = `productPage.${fieldPath}`;  // commonAttributes 없음!
        } else {
          fullFieldPath = `productPage.commonAttributes.${fieldPath}`;
        }

        this.applySingleFieldToAllOptionsWithValidation(fullFieldPath, value);
      } else {
        // OFF: pending에 추가만


        // 🔧 속성 타입에 따라 올바른 경로 사용
        let fullFieldPath;
        if (attributeType === 'exposed') {
          fullFieldPath = `productPage.commonAttributes.${fieldPath}`;
        } else if (attributeType === 'unexposed') {
          fullFieldPath = `productPage.${fieldPath}`;  // commonAttributes 없음!
        } else {
          fullFieldPath = `productPage.commonAttributes.${fieldPath}`;
        }

        this.addToPending(fullFieldPath, value);
      }
    },

    // ON 상태에서 즉시 저장 + 검증
    async applySingleFieldToAllOptionsWithValidation(fieldPath, value) {
      try {
        // 모든 옵션에 적용 (지연 저장)
        await this.applySingleFieldToAllOptions(fieldPath, value);
        // 자동 저장/자동 상태 업데이트는 비활성화됨
      } catch (error) {
        console.error('모든 옵션 적용 처리 중 오류:', error);
      }
    },

    // 단일 필드를 모든 옵션에 적용(지연 저장: 버튼 클릭 시 일괄 저장)
    applySingleFieldToAllOptions(fieldPath, value) {
      if (!this.product?.coupangDoc?.jsonDocument) {
        console.warn('🚫 applySingleFieldToAllOptions: coupangDoc.jsonDocument가 없음');
        return;
      }

      const optionCount = this.product.coupangDoc.jsonDocument.length;
      console.log(`🔧 [ON 모드][지연] 모든 옵션(${optionCount}개)에 적용: ${fieldPath} =`, value);

      // 모든 옵션에 해당 필드 적용 (현재 옵션 포함!)
      this.product.coupangDoc.jsonDocument.forEach((_, docIndex) => {
        const fullFieldPath = `coupangDoc.jsonDocument.${docIndex}.${fieldPath}`;
        this.pendingChanges[fullFieldPath] = value;
        console.log(`  📝 추가됨: ${fullFieldPath}`);
      });
      this.pendingChanges['updatedAt'] = Date.now();
      this.hasPendingChanges = true;

      console.log(`✅ [ON 모드] pendingChanges에 ${optionCount}개 필드 추가 완료`);
      return Promise.resolve();
    },

    // 객체로 변환된 unexposedAttributes를 원래 배열로 복원
    restoreUnexposedAttributesArray(brokenAttrs) {
      // 🔧 기존 값을 보존하면서 스키마에서 정의된 기본 속성들
      const defaultAttributes = [
        { attributeName: "사이즈", attributeValue: null },
        { attributeName: "세트여부", attributeValue: null }, // 🔧 하드코딩 제거
        { attributeName: "소리 유무", attributeValue: null },
        { attributeName: "캣닢/마따따비 향 유무", attributeValue: null },
        { attributeName: "권장 품종", attributeValue: null },
        { attributeName: "애완동물 장난감 종류", attributeValue: null }
      ];

      console.log('🔧 배열 복원 시작...');
      console.log('📋 손상된 객체:', brokenAttrs);

      // 기본 배열로 시작
      const restoredArray = [...defaultAttributes];

      // 손상된 객체에서 값 복원
      Object.keys(brokenAttrs).forEach(key => {
        const brokenItem = brokenAttrs[key];
        const index = parseInt(key);

        if (!isNaN(index) && brokenItem && typeof brokenItem === 'object') {
          // 인덱스가 유효한 범위 내에 있으면 값 복원
          if (index >= 0 && index < restoredArray.length) {
            if (brokenItem.attributeValue !== undefined) {
              restoredArray[index].attributeValue = brokenItem.attributeValue;
              console.log(`🔧 인덱스 ${index} 복원: ${restoredArray[index].attributeName} = ${brokenItem.attributeValue}`);
            }
          }
        }
      });

      console.log('✅ 배열 복원 완료:', restoredArray);
      return restoredArray;
    },

    // OFF 상태에서 pending에 변경사항 추가
    addToPending(fieldPath, value) {
      const currentOptionIndex = this.getCurrentOptionIndex();
      console.log(`📝 [OFF 모드] addToPending: ${fieldPath} = ${value} (옵션 인덱스: ${currentOptionIndex})`);

      // 🔧 unexposedAttributes 배열 요소 업데이트 시 전체 배열 업데이트
      if (fieldPath.includes('unexposedAttributes.') && fieldPath.includes('.attributeValue')) {

        // unexposedAttributes.1.attributeValue → 전체 배열 업데이트
        const match = fieldPath.match(/unexposedAttributes\.(\d+)\.attributeValue/);
        if (match) {
          const attrIndex = parseInt(match[1]);

          // 현재 배열 복사 (정규화된 배열 사용)
          const currentAttrs = [...(this.docData?.productPage?.unexposedAttributes || [])];

          // 해당 인덱스 값 업데이트
          if (currentAttrs[attrIndex]) {
            console.log(`🔄 [비노출속성 저장] ${currentAttrs[attrIndex].attributeName}: "${currentAttrs[attrIndex].attributeValue}" → "${value}"`);
            currentAttrs[attrIndex].attributeValue = value;

            // 전체 배열로 업데이트
            const fullFieldPath = `coupangDoc.jsonDocument.${currentOptionIndex}.productPage.unexposedAttributes`;
            this.pendingChanges[fullFieldPath] = currentAttrs;
            this.hasPendingChanges = true;
            console.log(`✅ [OFF 모드] unexposedAttributes 배열 전체 업데이트: ${fullFieldPath}`);
            console.log(`📋 [저장될 배열 확인]:`, currentAttrs.map(attr => `${attr.attributeName}="${attr.attributeValue}"`));
            return;
          }
        }
      }

      // 일반적인 필드 업데이트
      const fullFieldPath = `coupangDoc.jsonDocument.${currentOptionIndex}.${fieldPath}`;
      this.pendingChanges[fullFieldPath] = value;
      this.hasPendingChanges = true;
      console.log(`✅ [OFF 모드] pending에 추가: ${fullFieldPath} = ${value}`);
    },

    // 컴포넌트 이탈 시 pending 변경사항 일괄 저장
    async savePendingChanges() {
      if (!this.hasPendingChanges) {
        console.log('💾 저장할 pending 변경사항 없음');
        return false; // 명시적으로 false 리턴
      }

      console.log(`💾 Pending 변경사항 ${Object.keys(this.pendingChanges).length}개 일괄 저장 시작...`);
      console.log('📋 저장할 변경사항 상세:', JSON.stringify(this.pendingChanges, null, 2));
      console.log('🔧 applyToAllOptions 상태:', this.applyToAllOptions);

      // ✨ applyToAllOptions가 ON이면 pendingChanges를 전체 옵션으로 확장
      console.log('🔍 [디버깅] applyToAllOptions 체크:', this.applyToAllOptions, typeof this.applyToAllOptions);
      if (this.applyToAllOptions) {
        console.log('✅ [디버깅] expandPendingChangesToAllOptions() 호출 시작');
        this.expandPendingChangesToAllOptions();
        console.log('✅ [디버깅] expandPendingChangesToAllOptions() 호출 완료');
        console.log('📋 [디버깅] 확장 후 pendingChanges:', JSON.stringify(this.pendingChanges, null, 2));
      } else {
        console.log('❌ [디버깅] applyToAllOptions가 false이므로 확장하지 않음');
      }

      // 🔧 저장 직전에 "해당사항없음" 문자열을 null로 변환
      console.log('🔄 [저장 직전] 비노출속성 정규화 시작...');
      this.normalizeUnexposedAttributesInPendingChanges();

      // 사람이 고친 노출속성 값을 기록한다(coupangDoc 밖) — 견적 갱신이 되돌리지 않게(보고 3320E1). 자동 수정 칸을 합치기 전에 센다
      const userEdits = mergeUserEdits(this.product, this.pendingChanges);

      try {
        // 🔧 pending 변경사항을 임시로 적용한 데이터로 스키마 검증
        console.log('🔍 [savePendingChanges] 스키마 검증 시작...');
        const validationResult = this.validateAllOptionsSchemaWithPending();
        console.log('🔍 [savePendingChanges] 스키마 검증 결과:', {
          isValid: validationResult.isValid,
          completionRate: Math.round(validationResult.completionRate * 100) + '%',
          missingFieldsCount: validationResult.missingFields.length,
          errorsCount: validationResult.errors.length
        });

        // processing 은 «견적 작업이 실제로 도는 중» 이다 — 화면 저장 뒤 미완성은 일부완료(partially_completed)로 둔다.
        // (예전엔 완성도 80% 미만이면 processing 으로 덮어 «진행중» 깜빡임·등록 전송 막힘이 났다 — PR261016-0E5AEF·EF6713)
        const quotationStatus = validationResult.isValid ? 'completed' : 'partially_completed';

        console.log(`🔍 [savePendingChanges] 결정된 상태: ${quotationStatus}`);

        // 🔧 자동 수정된 필드들을 pending 변경사항에 추가
        if (validationResult.autoFixedFields && Object.keys(validationResult.autoFixedFields).length > 0) {
          console.log(`🔧 [자동 수정 필드 추가] ${Object.keys(validationResult.autoFixedFields).length}개 필드를 pending에 추가`);
          Object.assign(this.pendingChanges, validationResult.autoFixedFields);
        }

        // 🔧 pending 변경사항 + 상태 업데이트를 하나의 요청으로 합치기
        const updateFields = {
          ...this.pendingChanges,
          ...(userEdits.changed ? { quotationUserEdits: userEdits.edits } : {}),
          'quotationStatus': quotationStatus,
          'updatedAt': Date.now()
        };

        // 옵션 상태 업데이트 (스위치에 따라)
        if (this.product.options && this.product.options.length > 0) {
          if (this.applyToAllOptions) {
            // 모든 옵션 상태 업데이트
            this.product.options.forEach((_, optionIndex) => {
              updateFields[`options.${optionIndex}.quotationStatus`] = quotationStatus;
            });
          } else {
            // 저장 때 모든 옵션을 검사했으므로, 옵션마다 그 옵션의 검사 결과로 상태를 쓴다
            // (지금 보는 옵션 하나에만 상품 전체 상태를 쓰면 앞서 저장한 옵션들이 옛 상태로 남는다)
            const optionValidity = validationResult.optionValidity || [];
            this.product.options.forEach((_, optionIndex) => {
              if (typeof optionValidity[optionIndex] !== 'boolean') return;
              updateFields[`options.${optionIndex}.quotationStatus`] = optionValidity[optionIndex] ? 'completed' : 'partially_completed';
            });
          }
        }

        console.log('📤 서버로 전송할 updateFields (pending + 상태):', JSON.stringify(updateFields, null, 2));
        console.log('📋 상품 ID:', this.product._id);
        console.log('📋 상품 registrationId:', this.product.registrationId);

        // 🔧 하나의 API 호출로 pending 변경사항 + 상태 업데이트
        const result = await this.updateProductPartial({
          productId: this.product._id,
          updateFields: updateFields
        });

        console.log('📥 서버 응답 결과:', result);
        console.log(`✅ Pending 변경사항 + 상태 업데이트 완료: ${quotationStatus} (완성도: ${Math.round(validationResult.completionRate * 100)}%)`);

        // pending 상태 초기화
        this.pendingChanges = {};
        this.hasPendingChanges = false;

        // 저장 후 UI 데이터 동기화
        console.log('🔄 저장 후 UI 데이터 동기화 시작...');
        await this.initializeData();
        console.log('✅ UI 데이터 동기화 완료');

        console.log('✅ savePendingChanges 성공적으로 완료');
        return true; // 명시적으로 true 리턴

      } catch (error) {
        console.error('❌ Pending 변경사항 저장 중 오류:', error);
        throw error;
      }
    },

    // 비노출속성 드롭다운 변경 처리 (세트여부 추적 포함)
    onUnexposedAttributeChange(event, attr, index) {
      console.log(`🎯 [세트여부 추적] UI 드롭다운 변경: ${attr.attributeName} = ${event.target.value}`);

      attr.attributeValue = event.target.value === '' ? null : event.target.value;
      this.onAttributeDropdownChange('unexposed', index, attr.attributeValue);
    },

    // pendingChanges를 모든 옵션으로 확장 (ON 상태에서 저장 시 호출)
    expandPendingChangesToAllOptions() {
      if (!this.product?.coupangDoc?.jsonDocument) {
        console.warn('🚫 expandPendingChangesToAllOptions: coupangDoc.jsonDocument가 없음');
        return;
      }

      const expandedChanges = {};
      const optionCount = this.product.coupangDoc.jsonDocument.length;

      console.log(`🔄 [모든 옵션 적용] pendingChanges를 ${optionCount}개 옵션으로 확장 시작...`);

      Object.keys(this.pendingChanges).forEach(key => {
        // coupangDoc.jsonDocument.0.productPage.brand 형태에서 옵션 인덱스 추출
        const match = key.match(/^coupangDoc\.jsonDocument\.(\d+)\.(.+)$/);

        if (match) {
          const fieldPath = match[2]; // productPage.brand
          const value = this.pendingChanges[key];

          console.log(`  📝 필드 확장: ${fieldPath} = ${JSON.stringify(value)}`);

          // 모든 옵션에 동일한 값 적용
          for (let i = 0; i < optionCount; i++) {
            const newKey = `coupangDoc.jsonDocument.${i}.${fieldPath}`;
            expandedChanges[newKey] = value;
          }
        } else {
          // updatedAt 같은 일반 필드는 그대로 유지
          expandedChanges[key] = this.pendingChanges[key];
        }
      });

      this.pendingChanges = expandedChanges;
      console.log(`✅ [모든 옵션 적용] ${optionCount}개 옵션으로 확장 완료 (총 ${Object.keys(expandedChanges).length}개 필드)`);
    },

    // 현재 옵션에만 필드 저장(지연 저장: 버튼 클릭 시 저장)
    saveCurrentOptionField(fieldPath, value) {
      const currentOptionIndex = this.getCurrentOptionIndex();
      const fullFieldPath = `coupangDoc.jsonDocument.${currentOptionIndex}.${fieldPath}`;
      this.pendingChanges[fullFieldPath] = value;
      this.pendingChanges['updatedAt'] = Date.now();
      this.hasPendingChanges = true;
      console.log(`📝 [OFF 모드][지연저장] 현재 옵션(${currentOptionIndex}): ${fullFieldPath} =`, value);
    },

    // saveCurrentOptionFieldSilent 함수 제거됨 - pending 방식으로 대체

    // 중첩된 객체에 값 설정
    setNestedValue(obj, path, value) {
      const keys = path.split('.');
      let current = obj;

      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (!current[key] || typeof current[key] !== 'object') {
          this.$set(current, key, {});
        }
        current = current[key];
      }

      this.$set(current, keys[keys.length - 1], value);
    },

    // 현재 옵션의 인덱스 찾기
    getCurrentOptionIndex() {
      if (!this.selectedOption || !this.product?.options) return 0;

      const index = this.product.options.findIndex(opt =>
        opt.optionNumber === this.selectedOption.optionNumber
      );
      return index >= 0 ? index : 0;
    },

    // ===== 스키마 검증 함수들 =====

    // 스키마 검증 메인 함수 (기존 quotationService 함수들 활용)
    validateSchema() {
      if (!this.product?.rocketRegInfo?.schemaString) {
        return {
          isValid: false,
          completionRate: 0,
          missingFields: ['스키마 정보 없음'],
          errors: ['스키마 정보가 없습니다.']
        };
      }

      const validator = new PracticalSchemaValidator(this.product.rocketRegInfo.schemaString);

      if (!validator.schema) {
        return {
          isValid: false,
          completionRate: 0,
          missingFields: ['스키마 파싱 실패'],
          errors: ['스키마 파싱에 실패했습니다.']
        };
      }

      // 현재 옵션의 문서 데이터 검증
      const result = validator.validateDocument(this.docData);

      return {
        isValid: result.isValid,
        completionRate: result.completionRate || 0,
        missingFields: result.missingFields || [],
        errors: result.errors || [],
        totalFields: result.totalFields || 0,
        completedFields: result.completedFields || 0
      };
    },

    // 🔧 모든 옵션의 스키마 검증 (ON 모드용)
    validateAllOptionsSchema() {
      if (!this.schema || !this.product?.coupangDoc?.jsonDocument) {
        return {
          isValid: false,
          completionRate: 0,
          missingFields: ['스키마 또는 데이터 없음'],
          errors: ['스키마 정보가 없거나 데이터가 로드되지 않았습니다.']
        };
      }

      const allMissingFields = [];
      const allErrors = [];
      let totalValidOptions = 0;

      // 모든 옵션에 대해 검증 수행
      this.product.coupangDoc.jsonDocument.forEach((docData, index) => {
        const requiredFields = this.getRequiredFields();
        const optionMissingFields = [];
        const optionErrors = [];

        // 1. 기본 필수 필드 검증
        requiredFields.forEach(field => {
          const validation = this.validateFieldForData(field, docData);
          if (!validation.isValid) {
            optionMissingFields.push(`옵션${index + 1}: ${field.displayName}`);
            if (validation.error) {
              optionErrors.push(`옵션${index + 1}: ${validation.error}`);
            }
          }
        });

        // 2. 속성값 dropdown 검증
        const dropdownValidation = this.validateAttributeDropdownsForData(docData);
        if (!dropdownValidation.isValid) {
          optionMissingFields.push(...dropdownValidation.missingFields.map(field => `옵션${index + 1}: ${field}`));
          optionErrors.push(...dropdownValidation.errors.map(error => `옵션${index + 1}: ${error}`));
        }

        if (optionMissingFields.length === 0) {
          totalValidOptions++;
        }

        allMissingFields.push(...optionMissingFields);
        allErrors.push(...optionErrors);
      });

      // 3. 노출속성 중복 검증 (전체 옵션 대상)
      const duplicateValidation = this.validateExposedAttributesDuplication();
      if (!duplicateValidation.isValid) {
        allMissingFields.push('노출속성 중복');
        allErrors.push(duplicateValidation.error);
      }

      const totalOptions = this.product.coupangDoc.jsonDocument.length;
      const completionRate = totalOptions > 0 ? totalValidOptions / totalOptions : 0;
      const isValid = allMissingFields.length === 0;

      return {
        isValid,
        completionRate,
        missingFields: allMissingFields,
        errors: allErrors,
        totalFields: totalOptions,
        completedFields: totalValidOptions
      };
    },

    // 🔧 pending 변경사항을 적용한 데이터로 스키마 검증 (OFF 모드용)
    validateAllOptionsSchemaWithPending() {
      if (!this.schema || !this.product?.coupangDoc?.jsonDocument) {
        return {
          isValid: false,
          completionRate: 0,
          missingFields: ['스키마 또는 데이터 없음'],
          errors: ['스키마 정보가 없거나 데이터가 로드되지 않았습니다.']
        };
      }

      // 🔧 기존 데이터에 pending 변경사항을 임시로 적용한 데이터 생성
      const tempJsonDocument = this.applyPendingToData();

      const allMissingFields = [];
      const allErrors = [];
      let totalValidOptions = 0;
      const optionValidity = []; // 옵션별 검사 결과 (index = jsonDocument/options 순서)
      const autoFixedFields = {}; // 자동 수정된 필드들을 추적

      // 모든 옵션에 대해 검증 수행 (pending 적용된 데이터 사용)
      tempJsonDocument.forEach((docData, index) => {
        const requiredFields = this.getRequiredFields();
        const optionMissingFields = [];
        const optionErrors = [];

        console.log(`🔍 [스키마 검증] 옵션 ${index + 1} 검증 시작`);

        // 1. 기본 필수 필드 검증
        requiredFields.forEach(field => {
          const value = this.getNestedValue(docData, field.path);

          // 박스당 입수량 특별 처리: 0이거나 없으면 1로 설정
          if (field.path === 'logisticsPage.totalSKUsInBox' && (value === null || value === undefined || value === 0)) {
            console.log(`🔧 [자동 수정] ${field.displayName}: ${value} → 1 (자동 설정)`);
            this.setNestedValueInObject(docData, field.path, 1);
            // 자동 수정된 필드 추적
            autoFixedFields[`coupangDoc.jsonDocument.${index}.${field.path}`] = 1;
          }

          const validation = this.validateFieldForData(field, docData);
          console.log(`🔍 [필드 검증] ${field.displayName} (${field.path}): ${validation.isValid ? '✅ 통과' : '❌ 실패'}`);
          console.log(`🔍 [필드 값] ${field.displayName}: `, this.getNestedValue(docData, field.path));
          if (!validation.isValid) {
            optionMissingFields.push(`옵션${index + 1}: ${field.displayName}`);
            if (validation.error) {
              optionErrors.push(`옵션${index + 1}: ${validation.error}`);
            }
            console.log(`❌ [필드 검증 실패] ${field.displayName}: ${validation.error}`);
          }
        });

        // 2. 속성값 dropdown 검증
        const dropdownValidation = this.validateAttributeDropdownsForData(docData);
        console.log(`🔍 [드롭다운 검증] 옵션 ${index + 1}: ${dropdownValidation.isValid ? '✅ 통과' : '❌ 실패'}`);
        if (!dropdownValidation.isValid) {
          optionMissingFields.push(...dropdownValidation.missingFields.map(field => `옵션${index + 1}: ${field}`));
          optionErrors.push(...dropdownValidation.errors.map(error => `옵션${index + 1}: ${error}`));
          console.log(`❌ [드롭다운 검증 실패] 옵션 ${index + 1}:`, dropdownValidation.errors);
        }

        optionValidity[index] = optionMissingFields.length === 0;
        if (optionMissingFields.length === 0) {
          totalValidOptions++;
          console.log(`✅ [옵션 검증 완료] 옵션 ${index + 1}: 모든 검증 통과`);
        } else {
          console.log(`❌ [옵션 검증 실패] 옵션 ${index + 1}: 누락 필드 ${optionMissingFields.length}개`);
        }

        allMissingFields.push(...optionMissingFields);
        allErrors.push(...optionErrors);
      });

      // 3. 노출속성 중복 검증 (pending 적용된 데이터 대상)
      const duplicateValidation = this.validateExposedAttributesDuplicationWithData(tempJsonDocument);
      if (!duplicateValidation.isValid) {
        allMissingFields.push('노출속성 중복');
        allErrors.push(duplicateValidation.error);
        // 중복에 걸린 옵션은 그 옵션도 미완성으로 본다 (상품=일부완료인데 옵션=전부 완료로 찍히지 않게)
        (duplicateValidation.duplicateIndexes || []).forEach(i => { optionValidity[i] = false; });
      }

      const totalOptions = tempJsonDocument.length;
      const completionRate = totalOptions > 0 ? totalValidOptions / totalOptions : 0;
      const isValid = allMissingFields.length === 0;

      console.log(`🔍 [스키마 검증 결과] 총 옵션: ${totalOptions}, 유효 옵션: ${totalValidOptions}, 완성도: ${Math.round(completionRate * 100)}%`);
      console.log(`🔍 [스키마 검증 결과] 누락 필드: ${allMissingFields.length}개`, allMissingFields);
      console.log(`🔍 [스키마 검증 결과] 최종 유효성: ${isValid ? '✅ 유효' : '❌ 무효'}`);

      // 자동 수정된 필드가 있으면 로그 출력
      if (Object.keys(autoFixedFields).length > 0) {
        console.log(`🔧 [자동 수정된 필드] ${Object.keys(autoFixedFields).length}개:`, autoFixedFields);
      }

      return {
        isValid,
        completionRate,
        missingFields: allMissingFields,
        errors: allErrors,
        totalFields: totalOptions,
        completedFields: totalValidOptions,
        optionValidity,
        autoFixedFields: autoFixedFields // 자동 수정된 필드들 반환
      };
    },

    // 필수 필드 목록 정의
    getRequiredFields() {
      const fields = [
        // startPage 필수 필드
        {
          path: 'startPage.productName',
          displayName: '상품명',
          validator: (value) => value && value.trim().length > 0
        },

        // productPage 필수 필드
        {
          path: 'productPage.modelNumber',
          displayName: '모델번호',
          // 쿠팡 스키마상 «조건부 필수»(KC 인증 대상일 때만) — KC «해당사항없음»이면 비어 있어도 통과.
          // 견적 작업(quotationService)은 빈 모델번호로 completed 를 주므로, 여기서 막으면 화면 저장만으로 processing 으로 되돌아간다.
          validator: (value, doc) => {
            const kcMarkType = doc && doc.legalPage && doc.legalPage.kcMarkType;
            if (kcMarkType === '해당사항없음' && (value === null || value === undefined || value === '')) {
              return true;
            }
            if (!value || typeof value !== 'string' || value.trim().length === 0) {
              console.log('❌ 모델번호 검증 실패:', value);
              return false;
            }
            return true;
          }
        },
        {
          path: 'productPage.searchTags',
          displayName: '검색태그',
          validator: (value) => {
            if (!value || typeof value !== 'string' || value.trim().length === 0) {
              console.log('❌ 검색태그 검증 실패:', value);
              return false;
            }
            return true;
          }
        },
        {
          path: 'productPage.commonAttributes.purchasePrice',
          displayName: '공급가',
          validator: (value) => value !== null && value !== undefined && value > 0
        },
        {
          path: 'productPage.commonAttributes.coupangSalePrice',
          displayName: '판매가',
          validator: (value) => value !== null && value !== undefined && value > 0
        },

        // 이미지 관련 필수 필드
        {
          path: 'imagePage.images.mainImage',
          displayName: '메인 이미지',
          validator: (value) => value && value.trim().length > 0
        },
        {
          path: 'imagePage.details.altText',
          displayName: '대체텍스트',
          validator: (value) => value && value.trim().length > 0
        },

        // 노출속성 검증
        {
          path: 'productPage.commonAttributes.exposedAttributes',
          displayName: '노출속성',
          validator: (attributes) => {
            if (!Array.isArray(attributes)) return false;
            return attributes.every(attr =>
              attr.attributeName &&
              attr.attributeValue !== null &&
              attr.attributeValue !== undefined &&
              attr.attributeValue.toString().trim().length > 0
            );
          }
        },

        // legalPage 필수 필드
        {
          path: 'legalPage.kcMarkType',
          displayName: 'KC마크 타입',
          validator: (value) => value && value.trim().length > 0
        }
      ];

      // 스키마에 따라 동적으로 필수 필드 추가
      if (this.isFieldAllowedInSchema('logisticsPage', 'totalSKUsInBox')) {
        fields.push({
          path: 'logisticsPage.totalSKUsInBox',
          displayName: '박스당 입수량',
          validator: (value) => {
            // 값이 없거나 0인 경우 실패로 처리 (자동 수정은 별도 로직에서)
            if (value === null || value === undefined || value === 0) {
              console.log('❌ 박스당 입수량 검증 실패:', value);
              return false;
            }
            return value > 0;
          }
        });
      }

      return fields;
    },

    // 개별 필드 검증
    validateField(field) {
      try {
        const value = this.getNestedValue(this.docData, field.path);
        const isValid = field.validator(value, this.docData);

        return {
          isValid,
          error: isValid ? null : `${field.displayName}이(가) 누락되었거나 유효하지 않습니다.`
        };
      } catch (error) {
        return {
          isValid: false,
          error: `${field.displayName} 검증 중 오류: ${error.message}`
        };
      }
    },

    // 🔧 특정 데이터에 대한 개별 필드 검증 (모든 옵션 검증용)
    validateFieldForData(field, docData) {
      try {
        const value = this.getNestedValue(docData, field.path);
        const isValid = field.validator(value, docData);

        return {
          isValid,
          error: isValid ? null : `${field.displayName}이(가) 누락되었거나 유효하지 않습니다.`
        };
      } catch (error) {
        return {
          isValid: false,
          error: `${field.displayName} 검증 중 오류: ${error.message}`
        };
      }
    },

    // 중첩된 객체에서 값 가져오기
    getNestedValue(obj, path) {
      return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : undefined;
      }, obj);
    },

    // 🔧 원본 데이터에서 속성의 기존 값 가져오기 (변경 감지용)
    getOriginalAttributeValue(attributeType, index) {
      try {
        const currentOptionIndex = this.getCurrentOptionIndex();
        const originalDoc = this.product?.coupangDoc?.jsonDocument?.[currentOptionIndex];

        if (!originalDoc) return null;

        if (attributeType === 'exposed') {
          const exposedAttrs = originalDoc?.productPage?.commonAttributes?.exposedAttributes;
          return exposedAttrs?.[index]?.attributeValue || null;
        } else if (attributeType === 'unexposed') {
          const unexposedAttrs = originalDoc?.productPage?.unexposedAttributes;
          return unexposedAttrs?.[index]?.attributeValue || null;
        }

        return null;
      } catch (error) {
        console.error('getOriginalAttributeValue 오류:', error);
        return null;
      }
    },

    // 🔧 pending 변경사항을 기존 데이터에 임시로 적용한 데이터 생성
    applyPendingToData() {
      // 기존 데이터 깊은 복사
      const tempJsonDocument = this.deepClone(this.product.coupangDoc.jsonDocument);

      // 🔧 복사된 데이터에서 "해당사항없음" 문자열을 null로 변환
      console.log('🔄 [applyPendingToData] 임시 데이터 정규화 시작...');
      let normalizedCount = 0;
      tempJsonDocument.forEach((docData, index) => {
        if (docData?.productPage?.unexposedAttributes &&
            Array.isArray(docData.productPage.unexposedAttributes)) {
          docData.productPage.unexposedAttributes.forEach(attr => {
            if (attr.attributeValue === '해당사항없음') {
              console.log(`🔄 [임시 데이터 정규화] 옵션${index + 1} "${attr.attributeName}": "해당사항없음" → null`);
              attr.attributeValue = null;
              normalizedCount++;
            }
          });
        }
      });
      if (normalizedCount > 0) {
        console.log(`✅ [임시 데이터 정규화] ${normalizedCount}개 비노출속성을 null로 변환 완료`);
      }

      // pending 변경사항을 임시 데이터에 적용
      Object.keys(this.pendingChanges).forEach(fieldPath => {
        const value = this.pendingChanges[fieldPath];

        // coupangDoc.jsonDocument.0.productPage.unexposedAttributes 형태의 경로 파싱
        const match = fieldPath.match(/^coupangDoc\.jsonDocument\.(\d+)\.(.+)$/);
        if (match) {
          const optionIndex = parseInt(match[1]);
          const innerPath = match[2]; // productPage.unexposedAttributes

          if (tempJsonDocument[optionIndex]) {
            this.setNestedValueInObject(tempJsonDocument[optionIndex], innerPath, value);
          }
        }
      });

      return tempJsonDocument;
    },

    // 🔧 객체에 중첩된 값 설정 (Vue 반응성 없이)
    setNestedValueInObject(obj, path, value) {
      const keys = path.split('.');
      let current = obj;

      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (!current[key] || typeof current[key] !== 'object') {
          current[key] = {};
        }
        current = current[key];
      }

      current[keys[keys.length - 1]] = value;
    },

    // 노출속성 중복 검증 (quotationService 로직 기반)
    validateExposedAttributesDuplication() {
      if (!this.product?.coupangDoc?.jsonDocument) {
        return { isValid: true }; // 데이터가 없으면 통과
      }

      const exposedAttributesMap = new Map();
      const duplicateGroups = [];

      this.product.coupangDoc.jsonDocument.forEach((doc, index) => {
        if (doc?.productPage?.commonAttributes?.exposedAttributes) {
          const exposedAttrs = doc.productPage.commonAttributes.exposedAttributes;

          // 속성명과 속성값을 모두 포함한 키 생성
          const attrKey = JSON.stringify(exposedAttrs.map(attr => ({
            name: attr.attributeName,
            value: attr.attributeValue
          })).sort((a, b) => a.name.localeCompare(b.name)));

          if (exposedAttributesMap.has(attrKey)) {
            // 중복 발견
            const existingIndex = exposedAttributesMap.get(attrKey);
            let duplicateGroup = duplicateGroups.find(group => group.includes(existingIndex));
            if (!duplicateGroup) {
              duplicateGroup = [existingIndex];
              duplicateGroups.push(duplicateGroup);
            }
            duplicateGroup.push(index);
          } else {
            exposedAttributesMap.set(attrKey, index);
          }
        }
      });

      if (duplicateGroups.length > 0) {
        const duplicateInfo = duplicateGroups.map((group, groupIndex) =>
          `그룹 ${groupIndex + 1}: 옵션 ${group.map(idx => idx + 1).join(', ')}`
        ).join('; ');

        return {
          isValid: false,
          error: `노출속성이 완전히 동일한 옵션들이 있습니다. ${duplicateInfo}`
        };
      }

      return { isValid: true };
    },

    // 🔧 특정 데이터에 대한 노출속성 중복 검증 (pending 적용된 데이터용)
    validateExposedAttributesDuplicationWithData(jsonDocumentData) {
      if (!jsonDocumentData || !Array.isArray(jsonDocumentData)) {
        return { isValid: true };
      }

      const exposedAttributesMap = new Map();
      const duplicateGroups = [];

      jsonDocumentData.forEach((doc, index) => {
        if (doc?.productPage?.commonAttributes?.exposedAttributes) {
          const exposedAttrs = doc.productPage.commonAttributes.exposedAttributes;

          // 속성명과 속성값을 모두 포함한 키 생성
          const attrKey = JSON.stringify(exposedAttrs.map(attr => ({
            name: attr.attributeName,
            value: attr.attributeValue
          })).sort((a, b) => a.name.localeCompare(b.name)));

          if (exposedAttributesMap.has(attrKey)) {
            // 중복 발견
            const existingIndex = exposedAttributesMap.get(attrKey);
            let duplicateGroup = duplicateGroups.find(group => group.includes(existingIndex));
            if (!duplicateGroup) {
              duplicateGroup = [existingIndex];
              duplicateGroups.push(duplicateGroup);
            }
            duplicateGroup.push(index);
          } else {
            exposedAttributesMap.set(attrKey, index);
          }
        }
      });

      if (duplicateGroups.length > 0) {
        const duplicateInfo = duplicateGroups.map((group, groupIndex) =>
          `그룹 ${groupIndex + 1}: 옵션 ${group.map(idx => idx + 1).join(', ')}`
        ).join('; ');

        return {
          isValid: false,
          error: `노출속성이 완전히 동일한 옵션들이 있습니다. ${duplicateInfo}`,
          duplicateIndexes: [].concat(...duplicateGroups) // 중복에 걸린 옵션 index (옵션별 상태용)
        };
      }

      return { isValid: true };
    },

    // 속성값 dropdown 검증
    validateAttributeDropdowns() {
      const missingFields = [];
      const errors = [];

      // 노출속성 dropdown 검증
      if (this.docData?.productPage?.commonAttributes?.exposedAttributes &&
          Array.isArray(this.docData.productPage.commonAttributes.exposedAttributes)) {
        this.docData.productPage.commonAttributes.exposedAttributes.forEach((attr) => {
          const dropdownValues = this.getExposedAttributeOptions(attr.attributeName);

          if (dropdownValues && dropdownValues.length > 0) {
            if (!attr.attributeValue || !dropdownValues.includes(attr.attributeValue)) {
              missingFields.push(`노출속성 ${attr.attributeName}`);
              errors.push(`노출속성 "${attr.attributeName}"의 값이 허용된 옵션과 일치하지 않습니다. 허용값: ${dropdownValues.join(', ')}`);
            }
          } else if (!attr.attributeValue || attr.attributeValue.trim() === '') {
            missingFields.push(`노출속성 ${attr.attributeName}`);
            errors.push(`노출속성 "${attr.attributeName}"의 값이 입력되지 않았습니다.`);
          }
        });
      }

      // 🔧 비노출속성 dropdown 검증 (올바른 경로 사용)
      if (this.docData?.productPage?.unexposedAttributes &&
          Array.isArray(this.docData.productPage.unexposedAttributes)) {
        this.docData.productPage.unexposedAttributes.forEach((attr) => {
          const dropdownValues = this.getUnexposedAttributeOptions(attr.attributeName);

          if (dropdownValues && dropdownValues.length > 0) {
            if (attr.attributeValue && !dropdownValues.includes(attr.attributeValue)) {
              missingFields.push(`비노출속성 ${attr.attributeName}`);
              errors.push(`비노출속성 "${attr.attributeName}"의 값이 허용된 옵션과 일치하지 않습니다. 허용값: ${dropdownValues.join(', ')}`);
            }
          }
        });
      }

      return {
        isValid: missingFields.length === 0,
        missingFields,
        errors
      };
    },

    // 🔧 특정 데이터에 대한 속성값 dropdown 검증 (모든 옵션 검증용)
    validateAttributeDropdownsForData(docData) {
      const missingFields = [];
      const errors = [];

      // 노출속성 dropdown 검증
      if (docData?.productPage?.commonAttributes?.exposedAttributes &&
          Array.isArray(docData.productPage.commonAttributes.exposedAttributes)) {
        docData.productPage.commonAttributes.exposedAttributes.forEach((attr) => {
          const dropdownValues = this.getExposedAttributeOptions(attr.attributeName);

          if (dropdownValues && dropdownValues.length > 0) {
            if (!attr.attributeValue || !dropdownValues.includes(attr.attributeValue)) {
              missingFields.push(`노출속성 ${attr.attributeName}`);
              errors.push(`노출속성 "${attr.attributeName}"의 값이 허용된 옵션과 일치하지 않습니다. 허용값: ${dropdownValues.join(', ')}`);
            }
          } else if (!attr.attributeValue || attr.attributeValue.trim() === '') {
            missingFields.push(`노출속성 ${attr.attributeName}`);
            errors.push(`노출속성 "${attr.attributeName}"의 값이 입력되지 않았습니다.`);
          }
        });
      }

      // 비노출속성 dropdown 검증
      if (docData?.productPage?.unexposedAttributes &&
          Array.isArray(docData.productPage.unexposedAttributes)) {
        docData.productPage.unexposedAttributes.forEach((attr) => {
          const dropdownValues = this.getUnexposedAttributeOptions(attr.attributeName);

          console.log(`🔍 [비노출속성 검증] ${attr.attributeName}: 값="${attr.attributeValue}" (타입: ${typeof attr.attributeValue}), 드롭다운 옵션: ${dropdownValues?.length || 0}개`);

          if (dropdownValues && dropdownValues.length > 0) {
            // null, undefined, 빈 문자열은 유효한 값으로 처리 (해당사항없음)
            if (attr.attributeValue !== null &&
                attr.attributeValue !== undefined &&
                attr.attributeValue !== '' &&
                !dropdownValues.includes(attr.attributeValue)) {
              missingFields.push(`비노출속성 ${attr.attributeName}`);
              errors.push(`비노출속성 "${attr.attributeName}"의 값이 허용된 옵션과 일치하지 않습니다. 허용값: ${dropdownValues.join(', ')}`);
              console.log(`❌ [비노출속성 검증 실패] ${attr.attributeName}: "${attr.attributeValue}" (허용값: ${dropdownValues.join(', ')})`);
            } else {
              console.log(`✅ [비노출속성 검증 통과] ${attr.attributeName}: "${attr.attributeValue}"`);
            }
          }
        });
      }

      return {
        isValid: missingFields.length === 0,
        missingFields,
        errors
      };
    }
  }
}
</script>
<style scoped>
.quotation-edit {
  position: relative;
  padding: 15px;
  max-width: 1200px;
  margin: 0 auto;
}

.loading-container {
  text-align: center;
  padding: 40px 0;
}

.section {
  background-color: #f8f9fa;
  padding: 15px;
  border-radius: 4px;
  margin-bottom: 20px;
}

.tooltip-icon {
  font-size: 1.2rem;
  cursor: pointer;
  transition: color 0.2s;
}

.tooltip-icon:hover {
  color: #007bff !important;
}

.form-group label {
  font-weight: 500;
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
}

input.form-control,
select.form-control,
textarea.form-control {
  font-size: 0.9rem;
  padding: 0.375rem 0.5rem;
}

input:read-only {
  background-color: #f9f9f9;
}

.form-text.text-muted {
  font-size: 0.8rem;
  line-height: 1.2;
}

/* 개선된 토글 패널 스타일 */
.improved-toggle-panel {
  position: fixed;
  top: 50%;
  right: 60px; /* 40px에서 20px 더 왼쪽으로 이동 */
  transform: translateY(-50%);
  z-index: 1000;
}

.toggle-bubble {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 2px solid #dee2e6;
  border-radius: 25px;
  padding: 16px 20px;
  min-width: 140px;
  box-shadow:
    0 4px 15px rgba(0, 0, 0, 0.1),
    0 2px 4px rgba(0, 0, 0, 0.06);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  text-align: center;
}

.toggle-bubble:hover {
  transform: translateY(-2px);
  box-shadow:
    0 8px 25px rgba(0, 0, 0, 0.15),
    0 4px 10px rgba(0, 0, 0, 0.1);
}

.toggle-bubble.active {
  background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
  border-color: #28a745;
  box-shadow:
    0 4px 15px rgba(40, 167, 69, 0.2),
    0 2px 4px rgba(40, 167, 69, 0.1);
}

.toggle-title {
  font-size: 0.8rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 12px;
  line-height: 1.2;
}

.toggle-bubble.active .toggle-title {
  color: #155724;
}

.toggle-switch-area {
  display: flex;
  justify-content: center;
}

.bubble-switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 26px;
}

.bubble-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.bubble-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #6c757d 0%, #495057 100%);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 26px;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
}

.bubble-slider:before {
  position: absolute;
  content: "";
  height: 20px;
  width: 20px;
  left: 3px;
  bottom: 3px;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 50%;
  box-shadow:
    0 2px 6px rgba(0, 0, 0, 0.2),
    0 1px 2px rgba(0, 0, 0, 0.1);
}

input:checked + .bubble-slider {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  box-shadow:
    inset 0 2px 4px rgba(0, 0, 0, 0.1),
    0 0 10px rgba(40, 167, 69, 0.3);
}

input:checked + .bubble-slider:before {
  transform: translateX(24px);
  box-shadow:
    0 2px 8px rgba(0, 0, 0, 0.25),
    0 1px 3px rgba(0, 0, 0, 0.15);
}

/* 반응형 디자인 */
@media (max-width: 768px) {
  .improved-toggle-panel {
    right: 15px;
    top: auto;
    bottom: 20px;
    transform: none;
  }

  .toggle-bubble {
    min-width: 120px;
    padding: 12px 16px;
  }

  .toggle-title {
    font-size: 0.75rem;
    margin-bottom: 10px;
  }

  .bubble-switch {
    width: 45px;
    height: 24px;
  }

  .bubble-slider:before {
    height: 18px;
    width: 18px;
  }

  input:checked + .bubble-slider:before {
    transform: translateX(21px);
  }
}
</style>